<template>
  <v-card class="step-tab-panel" flat>
    <div class="assortment-table attributes-table d-flex flex-column">
      <!-- Tab title and search -->
      <v-container class="actions-container pa-0 flex-grow-0">
        <v-row class="ma-1" align="center">
          <v-col class="pa-0 text-no-wrap justify-start flex-grow-0 align-self-center mr-3">
            <!-- Page title -->
            <span class="font-weight-bold pl-3">{{ tkey('attributes.header') }}</span>

            <!-- Attribute editor tooltip -->
            <DocsLink link="toolguide/050-attributes.html" />
          </v-col>

          <v-col v-if="showProductsNotInTemplateWarning" class="pa-0 flex-grow-0 ml-2" cols="auto">
            <v-alert class="alert--small mr-5 mb-0" type="info" variant="tonal" density="compact">
              {{
                tkey('attributes.notInTemplateWarning', [
                  totalProductsNotInTemplate,
                  (productsData || []).length,
                ])
              }}
            </v-alert>
          </v-col>
          <v-col
            v-if="productsWithoutPurchasePriceInformation.length"
            class="mx-2 py-0"
            cols="auto"
          >
            <v-alert type="warning" closable text class="ma-1 pa-1 w-100 info-message">
              {{
                tkey('attributes.validation.notInAssortment', {
                  products: productsWithoutPurchasePriceInformation.length,
                })
              }}
            </v-alert>
          </v-col>

          <ValidationFilterControls
            v-if="showErrorControls"
            :model-value="validationFilter"
            :has-errors="tracker.gridHasErrors()"
            :has-warnings="tracker.gridHasWarnings()"
            @update:model-value="onValidationFilterChange"
          />

          <v-col class="pa-0 d-flex justify-end">
            <RtlsSearch
              v-if="gridApi"
              v-model="searchString"
              style="max-width: 500px"
              :placeholder="tkey('searchPlaceholder')"
              @update:model-value="gridApi.onFilterChanged()"
            />
          </v-col>
        </v-row>
      </v-container>

      <!-- Action panel -->
      <v-container class="actions-container pa-2 flex-grow-0">
        <v-row class="align-items-center ma-0">
          <v-col class="pa-0 d-flex display-start align-start buttons-header">
            <v-text-field
              id="number-of-products"
              v-model.number="newProductRowsNumber"
              data-id-e2e="txtNoOfNewProductsToAdd"
              :label="tkey('numberOfProducts')"
              single-line
              :rules="rules"
              style="max-width: 175px"
              :class="{ 'hidden-triangle': isTriangleHidden }"
              :disabled="isAddNewProductInputDisabled"
              @keyup.enter="isNewRowsNumberValid && addNewProducts()"
            />
            <v-btn
              id="btnAddNewProducts"
              primary
              data-id-e2e="btnAddNewProducts"
              class="float-left button-space-5"
              :disabled="isAddButtonDisabled"
              @click="addNewProducts"
            >
              {{ $t('actions.add') }}
            </v-btn>

            <v-btn
              v-if="hasRestOfMarketEnabled"
              data-id-e2e="btnAddRestOfMarketProducts"
              primary
              class="float-left button-space-5"
              @click="openRestOfMarketProductsModal"
            >
              {{ $t('actions.addROMProducts') }}
            </v-btn>
          </v-col>

          <!-- Map fields modal - gets displayed after uploaded data is received -->
          <MapFieldsModal
            v-if="isResetFromTemplate"
            :value="isResetFromTemplate"
            :is-reset-from-template="isResetFromTemplate"
            :download-file-url="'downloadFileUrl'"
            :upload-metadata="{ uploadMethod: 'scenario' }"
            :uploaded-data-details="uploadedDataDetails"
            :current-custom-fields="customHeaderNames"
            :fields-to-ignore="mergedFieldsToIgnore"
            enable-field-creation
            @confirm="processData"
            @cancel="closeResetDataModal"
          />

          <v-col class="pa-0 d-flex justify-end">
            <v-btn
              class="outlined-btn mr-2"
              variant="flat"
              secondary
              :disabled="isClearFiltersDisabled"
              @click="clearFilters"
            >
              {{ $t('actions.clearAllFilters') }}
            </v-btn>
            <ProductAttributeOptions
              v-if="hasTransferPriceEnabled"
              @sync-financials="handleSyncProductData"
              @update-maintain="bulkUpdateMaintain"
            />
            <v-btn
              v-if="showResetFromTemplateButton"
              :disabled="isEditingDisabled"
              class="mr-2 outlined-btn reset-kpi-button"
              variant="flat"
              secondary
              @click="openResetFromTemplateModal"
            >
              {{ tkey('attributes.resetProductsFromTemplateTitle') }}
            </v-btn>
            <v-btn
              :disabled="isEditingDisabled"
              class="mr-2 outlined-btn reset-kpi-button"
              variant="flat"
              secondary
              @click="openResetDataModal"
            >
              {{ tkey('attributes.resetProductKPIsTitle') }}
            </v-btn>
            <v-btn
              class="outlined-btn mr-2"
              variant="flat"
              secondary
              :disabled="!hasNewProducts || isEditingDisabled"
              @click="openDeleteProductsModal(getVisibleNewProductsToDelete())"
            >
              {{ $t('actions.removeNewProducts') }}
            </v-btn>
            <v-btn
              :disabled="isCleanAttributesDisabled"
              variant="flat"
              secondary
              class="outlined-btn mr-2"
              @click="openCleanAttributesModal"
            >
              {{ $t('actions.cleanAttributes') }}
            </v-btn>
            <v-btn
              v-if="
                hasMinimumFacingsEnabled ||
                hasMinimumDistributionEnabled ||
                hasAdditionalFieldsEnabled
              "
              class="mr-2"
              variant="flat"
              primary
              @click="openAddOptionalColumnsModal"
            >
              {{ tkey('attributes.addOptionalColumns') }}
            </v-btn>
            <div data-id-e2e="btnAddAttribute">
              <DataUpload
                :disabled="isEditingDisabled"
                :legends="csvUploadLegends"
                :csv-upload-handler="onCSVUpload"
                :source-upload-handler="onSourceDataUpload"
                :scenario-upload-handler="onScenarioDataUpload"
                :custom-attr-handler="onCustomAttrAdd"
                :furniture-upload-handler="onFurnitureUpload"
                :custom-fields="[...customHeaderNames, ...storeClassEligibilityHeaderNames]"
                :fields-to-ignore="mergedFieldsToIgnore"
                :products-data="productsData"
                :used-header-names="usedHeaders"
                show-modal
                enable-field-creation
                attributes-upload
                @process="process"
              />
            </div>
          </v-col>
        </v-row>
      </v-container>

      <!-- Products table -->
      <div class="h-100 flex-grow-1">
        <AgGridVue
          class="w-100 h-100"
          :loading="isGridLoading"
          :row-data="gridRowData"
          :grid-options="gridOptions"
          :does-external-filter-pass="doesExternalFilterPass"
          :stop-editing-when-cells-loses-focus="true"
          :get-context-menu-items="getContextMenuItems"
          @cell-value-changed="handleGridEdit"
          @grid-ready="onGridReady"
          @column-moved="onColumnMoved"
          @drag-stopped="saveColumnState"
        />
      </div>

      <!-- Actions -->
      <PageActions
        class="page-actions"
        show-export
        is-custom-export
        :has-data-changes="hasDataChanges"
        :has-data-errors="hasDataErrors"
        :save-disabled="isEditingDisabled"
        :is-discard-enabled="!isEditingDisabled"
        @export="exportCSV()"
        @discard="discard()"
        @save="saveChanges()"
      />
    </div>

    <!-- Dialogs and pop-ups -->

    <v-dialog
      v-model="isRenameCustomAttributeDialogOpen"
      retain-focus
      max-width="450"
      content-class="rtls-dialog"
      @keydown.esc="closeRenameCustomAttributeDialog"
      @click:outside="closeRenameCustomAttributeDialog"
    >
      <BaseDialogCard
        :title="tkey('attributes.renameAttributeDialog.title')"
        :border="true"
        @close="closeRenameCustomAttributeDialog"
      >
        <v-card-text>
          <v-text-field
            v-model="renamedCustomAttributeName"
            :rules="renameCustomAttributeRules"
            autofocus
            @keyup.enter="isRenameCustomAttributeValid && renameCustomAttribute()"
          />
        </v-card-text>

        <template #footer>
          <v-card-actions class="justify-center">
            <v-btn primary @click="closeRenameCustomAttributeDialog">
              {{ $t('actions.cancel') }}
            </v-btn>
            <v-btn
              secondary
              variant="flat"
              class="outlined-btn"
              :disabled="!isRenameCustomAttributeValid"
              @click="renameCustomAttribute"
            >
              {{ tkey('attributes.actions.renameAttr') }}
            </v-btn>
          </v-card-actions>
        </template>
      </BaseDialogCard>
    </v-dialog>

    <!-- This dialog is only needed for new products that are not yet saved to db -->
    <v-dialog
      v-model="isDeleteNewProductsDialogOpen"
      retain-focus
      max-width="450"
      content-class="rtls-dialog"
      @keydown.esc="closeDeleteNewProductsDialog"
      @click:outside="closeDeleteNewProductsDialog"
    >
      <BaseDialogCard
        ref="deleteDialog"
        :title="tkey('attributes.deleteProductDialog.title')"
        :border="true"
        @close="closeDeleteNewProductsDialog"
      >
        <v-card-text class="text-h6">
          <p>{{ tkey('attributes.deleteProductDialog.question') }}</p>
          <strong>{{ tkey('attributes.deleteProductDialog.warning') }}</strong>
        </v-card-text>

        <template #footer>
          <v-card-actions class="justify-center">
            <v-btn primary @click="closeDeleteNewProductsDialog">
              {{ $t('actions.cancel') }}
            </v-btn>
            <v-btn secondary variant="flat" class="outlined-btn" @click="deleteRows">
              {{ tkey('attributes.actions.deleteProduct') }}
            </v-btn>
          </v-card-actions>
        </template>
      </BaseDialogCard>
    </v-dialog>

    <RestOfMarketProductsModal
      v-if="hasRestOfMarketEnabled && isRestOfMarketProductsModalOpen"
      :products-to-exclude="getProductsToExclude()"
      :product-category-key="commonProductCategoryLevelKey"
      :value="isRestOfMarketProductsModalOpen"
      @close="closeRestOfMarketProductsModal"
      @add-selected-products="addROMProducts"
    />

    <AddOptionalColumnsModal
      :value="isAddOptionalColumnsModalOpen"
      :optional-attrs="optionalAttributes"
      @add-optional-columns="addOptionalColumns"
      @close="closeAddOptionalColumnsModal"
    />

    <ResetProductDataModal
      :value="isResetDataModalOpen"
      :data-to-reset-enum="fieldEnum"
      @reset-product-data="resetProducts"
      @close="closeResetDataModal"
    />

    <CleanAttributesModal
      :value="isCleanAttributesModalOpen"
      :custom-attributes="currentCustomAttributes"
      :attributes-to-clean="attributesToClean"
      @process="processCleanAttributesConfig"
      @clean-attributes="cleanCustomAttributesInEditor"
      @close="closeCleanAttributesModal"
    />

    <AttributeGroupingModal
      :value="groupingModalOpen"
      :attribute-values="groupingValues"
      :grouping-column="groupingColumn"
      :used-headers="usedHeaders"
      @close="closeGroupingModal"
      @create-attribute-group="createGroupedAttr"
      @keydown.esc="groupingModalOpen = false"
    />

    <DependencyTreeFeedbackModal
      :value="dependencyTreeModalOpen"
      :results="dependencyTreeFeedback"
      :warning-message="dependencyTreeWarningMessage"
      page="productAttributeEditor"
      @close="closeDependencyTreeModal"
      @commit="saveChanges(true)"
    />

    <UnsavedDataModal
      ref="unsavedDataModal"
      :value="isUnsavedDataModalOpen"
      @cancel="closeUnsavedDataModal"
      @confirm="confirmUnsavedDataModal"
    />
  </v-card>
</template>

<script>
import to from 'await-to-js';
import {
  cloneDeep,
  compact,
  debounce,
  difference,
  each,
  every,
  filter,
  find,
  flattenDeep,
  get,
  groupBy,
  has,
  head,
  includes,
  intersectionWith,
  isEmpty,
  isEqual,
  isInteger,
  isNil,
  isString,
  isUndefined,
  keyBy,
  keys,
  map,
  mapValues,
  merge,
  min,
  omit,
  omitBy,
  pickBy,
  range,
  reduce,
  set,
  size,
  some,
  toLower,
  toUpper,
  trim,
  union,
  uniq,
  values,
  maxBy,
  entries,
  pick,
} from 'lodash';
import { mapActions, mapState } from 'pinia';

// enums
import productOriginSourceTypes from '@enums/origin-source-types';
import pgMustFlagOptions from '@enums/pg-must-flag-option';
import maintainOptions from '@enums/price-change-maintain-option';
import productModellingTypes from '@enums/product-modelling-types';
import uploadMethods from '@enums/productAttributes/attribute-upload-methods';
import OptionalAttributeEditorAttributesEnums from '@enums/productAttributes/optional-attributes';
import sizeAttributes from '@enums/size-attributes';
import WorkpackageType from '@enums/workpackage-type';

// utils, plugins and helpers
import { jobApi } from '@/helpers';
import exportCSV from '@/mixins/export-csv'; // TODO remove
import agGridUtils from '@/utils/ag-grid-utils';
import gridPreferences from '@/utils/grid-preferences';
import formatUtils from '@/utils/number-format-utils';
import productUtils from '@/utils/product-utils';
import { renderProductSummaryCell } from '@/webshell-embed/product-summary-cell-renderer';
import { shellBridge } from '@/webshell-embed/platform-bridge';

// composables
import { useInputValidations } from '@/composables/input-validations';
import { useUnsavedDataWarning } from '@/composables/unsaved-data-warning';
import { useDayjs } from '@/composables/use-dayjs';
import { useInternationalization } from '@/composables/use-internationalization';
import { usePermission } from '@/composables/use-permission';
import { useFormatNumber } from '@/composables/use-format-number';
import { useFeature } from '@/composables/use-feature';
import { useProductIndicators } from '@/composables/product-indicators';
import { useScenarioStatusFormatter } from '@/composables/use-scenario-status-formatter';
import { getCurrentInstance } from 'vue';
import { useGridTracker } from '@/composables/use-grid-state';
import { useAiAgentActions } from '@/composables/use-ai-agent-actions';

// stores
import { useAiStore } from '@/stores/ai';
import { useContextStore } from '@/stores/context';
import { useFilesStore } from '@/stores/files';
import { useFurnitureStore } from '@/stores/furniture';
import { useScenarioProductsStore } from '@/stores/scenario-products';
import { useScenariosStore } from '@/stores/scenarios';
import { useSnackbarStore } from '@/stores/snackbar';
import { useToolDataStore } from '@/stores/tool-data';
import { useWorkpackagesStore } from '@/stores/workpackages';

// other components
import AgGridIcon from '@/components/AgGridIcon.vue';
import CellMarginEditor from '@/components/CellMarginEditor.vue';
import CellUnitSuffixRenderer from '@/components/CellUnitSuffixRenderer.vue';
import ComboboxEditor from '@/components/ComboboxEditor.vue';
import ImageRenderer from '@/components/ImageRenderer.vue';
import MaskDateEditor from '@/components/MaskDateEditor.vue';
import severity from '@/validation/severity';
import attributeEditorValidation from '@sharedModules/data/utils/attribute-editor-validation';
import customAttributesHeaderGroup from './table-components/CustomAttributesHeaderGroup.vue';

import { numericColumn } from '../../../../utils/grid-utils/numericColumn';

// We need to do translations inside an ag-grid functions where this is not bound to Vue.
const localizationKey = 'scenarioInputPage';

const PAE_COLUMN_STATE_PREFIX = 'attributeEditorColumnState';
const PAE_COLUMN_STATE_VERSION = 6;

const maximumRowsToAdd = 1000;

const fieldEnum = {
  isNewProduct: 'isNewProduct',
  inReset: 'inReset',
  productKey: 'productKey',
  productKeyDisplay: 'productKeyDisplay',
  itemDescription: 'itemDescription',
  contentValue: 'contentValue',
  contentUnitOfMeasure: 'contentUnitOfMeasure',
  futureId: 'futureId',
  margin: 'margin',
  theoreticalMargin: 'theoreticalMargin',
  price: 'price',
  theoreticalPrice: 'theoreticalPrice',
  unitWidth: 'unitWidth',
  unitLength: 'unitLength',
  unitHeight: 'unitHeight',
  productSeries: 'productSeries',
  recentlySold: 'recentlySold',
  assortment: 'assortment',
  parentProduct: 'parentProduct',
  deleteNewProduct: 'deleteNewProduct',
  fromTemplate: 'fromTemplate',
  purchasePriceInRange: 'purchasePrice',
  originalPurchasePrice: 'originalPurchasePrice',
  transferPriceInRange: 'transferPrice',
  originalTransferPrice: 'originalTransferPrice',
  maintainOption: 'maintainOption',
  originalMargin: 'originalMargin',
  originalPrice: 'originalPrice',
  palletContent: 'palletContent',
  averageTransactionWeight: 'averageTransactionWeight',
  kpku: 'kpku',
  storeClassEligibility: 'storeClassEligibility',
  packageTypeDescription: 'packageTypeDescription',
  periodID: 'periodID',
  clientProductKey: 'clientProductKey',
  shrinkToSalesRatio: 'shrinkToSalesRatio',
  originalShrinkToSalesRatio: 'originalShrinkToSalesRatio',
  dependentSource: 'dependentSource',
};

const fieldImportExportPolicy = {
  [fieldEnum.maintainOption]: { ignoreForExport: true, ignoreForImport: true },
};

const ignoredFieldIdsForExport = new Set(keys(pickBy(fieldImportExportPolicy, 'ignoreForExport')));
const ignoredFieldIdsForImport = keys(pickBy(fieldImportExportPolicy, 'ignoreForImport'));

const tracker = useGridTracker();

function hasValidationError(params) {
  const level = tracker.getHighestSeverity(params);
  return level === severity.block || level === severity.error;
}

const validationCellClassRules = {
  'cell-error': hasValidationError,
  'cell-warning': params => tracker.getHighestSeverity(params) === severity.warn,
};

const reactions = {
  parentProduct() {
    return { assortment: false };
  },
  palletContent() {
    return { assortment: false };
  },
  theoreticalPrice() {
    return { maintainOption: maintainOptions.manuallySet };
  },
  theoreticalMargin() {
    return { maintainOption: maintainOptions.manuallySet };
  },
};

export default {
  components: {
    /* eslint-disable vue/no-unused-components */
    ImageRenderer,
    ComboboxEditor,
    CellUnitSuffixRenderer,
    customAttributesHeaderGroupComponent: customAttributesHeaderGroup,
    /* eslint-disable vue/no-unused-components */
    CellMarginEditor,
    MaskDateEditor,
  },

  mixins: [exportCSV],

  beforeRouteLeave(routeTo, from, next) {
    this.beforeRouteLeave(routeTo, from, next);
  },
  setup() {
    const { canEditAttributeEditorPage, canEditClientID } = usePermission();

    const {
      betaClientIDEditEnabled,
      hasProductsExistingFromNewInTemplateEnabled,
      hasClientProductKeyColumnEnabled,
      templatesEnabled,
      gridContextMenusEnabled,
      dependentProductsEnabled,
      aiPanelEnabled,
    } = useFeature();

    const { formatScenarioProgress } = useScenarioStatusFormatter('attribute-editor');

    const instance = getCurrentInstance();

    // ─── AI Tool Helpers ──────────────────────────────────────────────────────
    // Shared by get-product-by-key and get-visible-products.
    //
    // Returns an ordered array of { colId, headerName } for all currently
    // visible grid columns (respects feature flags and hidden columns), plus a
    // synthetic hasErrors entry appended last.
    const getVisibleProductFields = () => {
      const gridApi = instance.proxy.gridApi;
      if (!gridApi) return [];
      const cols = gridApi.getColumns() || [];
      const fields = cols
        .filter(col => {
          if (!col.isVisible()) return false;
          const colId = col.getColId();
          const colDef = col.getColDef();
          if (!colDef.headerName?.trim()) return false; // skip icon/action columns
          if (colId.startsWith('restructuredEligibility')) return false; // store-class sub-columns
          return true;
        })
        .map(col => ({ colId: col.getColId(), headerName: col.getColDef().headerName }));
      fields.push({ colId: '_hasErrors', headerName: 'hasErrors' });
      return fields;
    };

    // ─── Validation issue helpers ─────────────────────────────────────────────
    // The tracker owns validation state: state.invalidCells is
    // { rowKey: { fieldId: { block: [issue], error: [issue], warn: [issue] } } }.

    // All issues a row currently has, or null when the row is valid.
    const getRowIssues = productKey => tracker.state.invalidCells[String(productKey)] || null;

    // Flattens one cell entry to [{ severity, message }], block → error → warn,
    // translating each message the same way the cell tooltip does.
    const flattenCellIssues = cellEntry =>
      ['block', 'error', 'warn'].flatMap(severityClass =>
        (cellEntry[severityClass] ?? []).map(issue => {
          const path = `scenarioInputPage.attributes.validation.${issue.message}`;
          const translated = instance.proxy.t(path, issue.params);
          return {
            severity: severityClass,
            message: translated !== path ? translated : issue.message,
          };
        })
      );

    // Encodes an array of product rows as a TOON tabular string.
    // Format:  products[N]{col1,col2,...}:\n  val1,val2,...
    // Values containing commas, double-quotes, or newlines are CSV-escaped.
    const buildProductToonTable = (products, fields) => {
      const escape = val => {
        if (val == null) return '';
        const s = String(val);
        return s.includes(',') || s.includes('"') || s.includes('\n') || s.includes('\r')
          ? `"${s.replace(/"/g, '""')}"`
          : s;
      };
      const header = `products[${products.length}]{${fields.map(f => f.colId).join(',')}}:`;
      const rows = products.map(
        p =>
          `  ${fields
            .map(f =>
              f.colId === '_hasErrors' ? escape(!!getRowIssues(p.productKey)) : escape(p[f.colId])
            )
            .join(',')}`
      );
      return [header, ...rows].join('\n');
    };

    // Resolves and coerces a single field update for apply-product-updates and bulk-set-visible-column.
    // Returns { fieldId, coercedValue } on success, or { error: string } on failure.
    const coerceProductFieldUpdate = (fieldName, rawValue) => {
      const excludedFields = new Set([
        'storeClassEligibility',
        'parentProduct',
        'isNewProduct',
        'fromTemplate',
        'deleteNewProduct',
        'dependentSource',
      ]);
      const booleanFields = new Set(['assortment', 'inReset', 'recentlySold']);
      const integerFields = new Set(['palletContent', 'periodID', 'contentValue']);
      const validStandardFields = new Set(Object.values(fieldEnum));
      const customAttrFields = instance.proxy.customAttributeColumnsByField || {};

      let resolvedField = fieldName;
      if (!customAttrFields[fieldName] && !validStandardFields.has(fieldName)) {
        const byDisplayName = Object.values(customAttrFields).find(
          col => col.headerName?.toLowerCase() === fieldName.toLowerCase()
        );
        if (byDisplayName) resolvedField = byDisplayName.field;
      }

      if (excludedFields.has(resolvedField)) {
        return { error: `'${fieldName}' cannot be edited via this action` };
      }
      const isStandard = validStandardFields.has(resolvedField);
      const isCustom = !!customAttrFields[resolvedField];
      if (!isStandard && !isCustom) {
        return { error: `'${fieldName}' is an unknown field` };
      }

      let coercedValue;
      if (resolvedField === 'maintainOption') {
        if (rawValue === 'null' || rawValue === '') {
          coercedValue = null;
        } else {
          const validOptions = Object.values(maintainOptions).filter(v => v !== null);
          if (!validOptions.includes(rawValue)) {
            return {
              error: `'${fieldName}': invalid maintainOption "${rawValue}". Valid: ${validOptions.join(', ')}, null`,
            };
          }
          coercedValue = rawValue;
        }
      } else if (booleanFields.has(resolvedField)) {
        coercedValue = rawValue === 'true' || rawValue === true;
      } else if (integerFields.has(resolvedField)) {
        coercedValue = parseInt(rawValue, 10);
        if (Number.isNaN(coercedValue))
          return { error: `'${fieldName}': "${rawValue}" is not a valid integer` };
      } else if (isCustom) {
        coercedValue = rawValue;
      } else {
        const asFloat = parseFloat(rawValue);
        coercedValue = Number.isNaN(asFloat) ? rawValue : asFloat;
      }
      return { fieldId: resolvedField, coercedValue };
    };

    // Parses a TOON-formatted table string (as produced by buildProductToonTable or constructed by the
    // LLM) into { columns, rows }. Accepts any prefix before [N]{col1,col2,...}: in the header line.
    // Quoted CSV values (RFC 4180-style "" escaping) are fully supported.
    // Returns { columns: string[], rows: string[][] } on success, or { error: string }.
    const parseToonTable = toon => {
      if (!toon || typeof toon !== 'string') {
        return { error: 'input is missing or not a string' };
      }
      const lines = toon.split('\n');
      const headerMatch = lines[0]?.trim().match(/\{([^}]+)\}:/);
      if (!headerMatch) {
        return { error: 'header line must match *[N]{col1,col2,...}:' };
      }
      const columns = headerMatch[1].split(',').map(c => c.trim());

      const parseRow = line => {
        const raw = line.replace(/^ {1,2}/, '');
        const cells = [];
        let pos = 0;
        while (pos <= raw.length) {
          if (pos === raw.length) {
            cells.push('');
            break;
          }
          if (raw[pos] === '"') {
            let val = '';
            pos += 1;
            while (pos < raw.length) {
              if (raw[pos] === '"') {
                if (raw[pos + 1] === '"') {
                  val += '"';
                  pos += 2;
                } else {
                  pos += 1;
                  break;
                }
              } else {
                val += raw[pos];
                pos += 1;
              }
            }
            cells.push(val);
            if (raw[pos] === ',') pos += 1;
          } else {
            const end = raw.indexOf(',', pos);
            if (end === -1) {
              cells.push(raw.slice(pos));
              break;
            }
            cells.push(raw.slice(pos, end));
            pos = end + 1;
          }
        }
        return cells;
      };

      const rows = lines
        .slice(1)
        .filter(line => line?.trim())
        .map(line => parseRow(line));
      return { columns, rows };
    };

    // Resolves the field list to use for a product table tool call.
    // If `columns` is provided, filters to matching fields by headerName or colId
    // (case-insensitive). productKeyDisplay and hasErrors are always included.
    // Returns { fields, unrecognised } where unrecognised lists any column names
    // that could not be matched.
    const resolveProductFields = columns => {
      const allFields = getVisibleProductFields();
      if (!columns?.length) return { fields: allFields, unrecognised: [] };

      const alwaysInclude = new Set(['productKeyDisplay', '_hasErrors']);
      const requested = columns.map(c => c.toLowerCase().trim());
      const matched = new Set();

      const filtered = allFields.filter(f => {
        if (alwaysInclude.has(f.colId)) return true;
        const headerLower = f.headerName.toLowerCase();
        const colIdLower = f.colId.toLowerCase();
        const hit = requested.findIndex(r => r === headerLower || r === colIdLower);
        if (hit !== -1) {
          matched.add(requested[hit]);
          return true;
        }
        return false;
      });

      const unrecognised = requested.filter(r => !matched.has(r));
      return { fields: filtered, unrecognised };
    };

    // Returns an error string when encoded output exceeds the safe output cap,
    // null otherwise. ~80 k chars ≈ 20 k tokens at ~4 chars/token.
    const checkProductOutputCap = (encoded, productCount) => {
      const CAP = 80_000;
      if (encoded.length <= CAP) return null;
      const charsPerProduct = productCount > 0 ? Math.round(encoded.length / productCount) : 0;
      return (
        `[Tool] Too much data to return (${productCount} products × ~ ${charsPerProduct} chars/product). ` +
        'Narrow your request to fewer products or apply a more specific filter first.'
      );
    };

    useAiAgentActions({
      allowScenarioWorkflowNavigation: true,
      getExclude: () => {
        if (instance.proxy.isEditingDisabled) {
          return [
            'apply-product-updates',
            'bulk-set-visible-column',
            'add-feed-attribute',
            'add-custom-attribute',
            'delete-custom-attribute',
            'save-changes',
            'discard-changes',
          ];
        }
        const customAttrs = Object.values(instance.proxy.currentCustomAttributes || {});
        return customAttrs.length ? [] : ['delete-custom-attribute'];
      },
      getSupplementary: () => {
        const customAttrs = Object.values(instance.proxy.currentCustomAttributes || {});
        const customAttrNames = customAttrs.map(a => a.name);
        const customAttrFieldDescriptions =
          customAttrs.map(a => `${a.name} (field id: ${a.id})`).join(', ') || 'none';
        const standardFieldNames = Object.values(fieldEnum)
          .filter(
            f =>
              ![
                'storeClassEligibility',
                'parentProduct',
                'deleteNewProduct',
                'dependentSource',
              ].includes(f)
          )
          .join(', ');
        return {
          'apply-product-updates': `Standard fields: ${standardFieldNames}. Custom attribute fields: ${customAttrFieldDescriptions}`,
          'bulk-set-visible-column': `Standard fields: ${standardFieldNames}. Custom attribute fields: ${customAttrFieldDescriptions}`,
          'delete-custom-attribute': customAttrNames.length
            ? `Available custom attributes: ${customAttrNames.join(', ')}`
            : 'No custom attributes are currently defined.',
        };
      },
      actions: {
        // ─── DATA FEED ──────────────────────────────────────────────────
        'get-feed-attributes': {
          readOnly: true, // data fetch only — no confirmation prompt needed
          description:
            'Fetch the full list of attributes available in the data feed for this scenario. ' +
            'Use this when the user asks what feed attributes are available to add. ' +
            'Do not confirm success in your message text; the system will confirm or report errors after the action completes. ' +
            'The result will arrive as an automated [Tool] message fetched by the system — it was NOT entered by the user. ' +
            'The result includes numeric attribute keys (e.g. "key: 18") — these are internal system identifiers for your use only; never mention or display them to the user. ' +
            'After receiving the tool result, report the sources and total count concisely — do not enumerate every attribute unless the user explicitly asks. ' +
            'Do not proactively recommend specific attributes to add; wait for the user to ask.',
          workingLabel: 'Fetching feed attributes',
          parameters: { type: 'object', properties: {}, required: [] },
          handler: async () => {
            const toolDataStore = useToolDataStore();
            const productKeys = instance.proxy.productsData?.map(p => p.productKey) ?? [];

            const sources = await toolDataStore.getProductAttributeSources();
            if (!sources?.length) {
              return '[Tool] No feed attribute sources found for this scenario.';
            }

            const perSourceResults = await Promise.all(
              sources.map(source =>
                toolDataStore.getProductAttributesBySource({ attributeSource: source, productKeys })
              )
            );

            const total = perSourceResults.reduce((sum, attrs) => sum + (attrs?.length ?? 0), 0);
            const lines = [`[Tool] Available feed attributes (${total} total):`, ''];

            sources.forEach((source, i) => {
              const attrs = perSourceResults[i] ?? [];
              lines.push(`Source: ${source}`);
              attrs.forEach(attr => lines.push(`- ${attr.attributeName}`));
              lines.push('');
            });

            lines.push('Use the add-feed-attribute tool to add one of these attributes.');

            return lines.join('\n');
          },
        },

        'add-feed-attribute': {
          description:
            'Add an attribute from the data feed to the scenario. ' +
            'First use get-feed-attributes to determine available attribute names and source names. ' +
            'The attribute name and source name must match exactly (case-sensitive) what was returned by get-feed-attributes. ' +
            'Do not confirm success in your message text; the system will confirm or report errors.',
          workingLabel: 'Adding feed attribute',
          confirmationLabel: args =>
            `add feed attribute "${args.attributeName}" from "${args.sourceName}"`,
          parameters: {
            type: 'object',
            properties: {
              attributeName: {
                type: 'string',
                description: 'Exact attribute name as returned by get-feed-attributes.',
              },
              sourceName: {
                type: 'string',
                description: 'Exact source name as returned by get-feed-attributes.',
              },
            },
            required: ['attributeName', 'sourceName'],
          },
          handler: async ({ attributeName, sourceName }) => {
            if (!canEditAttributeEditorPage.value) {
              return '[Tool] Action failed: you do not have permission to edit this page.';
            }
            if (instance.proxy.isGridLoading) {
              return '[Tool] Action failed: the grid is currently loading, please wait and try again.';
            }

            if (!attributeName?.trim() || !sourceName?.trim()) {
              return '[Tool] Action failed: both attributeName and sourceName are required.';
            }

            if (instance.proxy.usedHeaders.includes(attributeName)) {
              return (
                `[Tool] Action failed: a column named "${attributeName}" already exists in this scenario. ` +
                'Note: if you recently deleted this attribute without saving, the name is still reserved until you save.'
              );
            }

            const toolDataStore = useToolDataStore();
            const sources = await toolDataStore.getProductAttributeSources();
            if (!sources?.includes(sourceName)) {
              const available = sources?.join(', ') || 'none';
              return `[Tool] Action failed: unknown source "${sourceName}". Available sources: ${available}. Use get-feed-attributes to see all options.`;
            }

            const mappings = [
              { key: 'Product Key', mongoField: 'productKeyDisplay', selectedField: 'Product Key' },
              {
                key: `${sourceName} - ${attributeName}`,
                selectedField: `${sourceName} - ${attributeName}`,
                isNew: true,
                mongoField: null,
              },
            ];

            try {
              await instance.proxy.process({
                fileId: undefined,
                mappings,
                attributeSource: sourceName,
                attributeNames: [attributeName],
                uploadMethod: 'feed',
              });
              return `[Tool] Feed attribute "${attributeName}" from source "${sourceName}" added. The column is now visible in the grid. Changes are unsaved.`;
            } catch (err) {
              return `[Tool] Action failed: ${err?.message || 'an unknown error occurred while adding the attribute.'}`;
            }
          },
        },

        'add-custom-attribute': {
          description:
            'Add a new blank custom attribute column to the scenario. ' +
            'Use this when the user wants to manually enter attribute values rather than pull from a data feed. ' +
            'If a name is already in use (including recently-deleted-but-unsaved names), the action will fail. ' +
            'Do not confirm success in your message text; the system will confirm or report errors.',
          workingLabel: 'Adding custom attribute',
          confirmationLabel: args => `add custom attribute "${args.value}"`,
          parameters: {
            type: 'object',
            properties: {
              value: {
                type: 'string',
                description: 'The name for the new custom attribute column.',
              },
            },
            required: ['value'],
          },
          handler: async ({ value }) => {
            if (!canEditAttributeEditorPage.value) {
              return '[Tool] Action failed: you do not have permission to edit this page.';
            }
            if (instance.proxy.isGridLoading) {
              return '[Tool] Action failed: the grid is currently loading, please wait and try again.';
            }
            if (!value?.trim()) {
              return '[Tool] Action failed: attribute name cannot be empty.';
            }
            if (instance.proxy.usedHeaders.includes(value.trim())) {
              return (
                `[Tool] Action failed: a column named "${value.trim()}" already exists. ` +
                'Note: if you recently deleted this attribute without saving, the name is still reserved until you save.'
              );
            }

            instance.proxy.isGridLoading = true;
            try {
              await instance.proxy.onCustomAttrAdd(value.trim());
              return `[Tool] Custom attribute column "${value.trim()}" added. The column is now visible in the grid. Changes are unsaved.`;
            } catch (err) {
              return `[Tool] Action failed: ${err?.message || 'an unknown error occurred while adding the attribute.'}`;
            } finally {
              instance.proxy.isGridLoading = false;
            }
          },
        },

        // ─── READ / EXPLORE ──────────────────────────────────────────────
        'get-product-by-key': {
          readOnly: true,
          description:
            'Get field values for one or more specific products by their exact productKeyDisplay. ' +
            'Pass as many keys as needed in a single call. ' +
            'Matching is exact — no partial or description matching. ' +
            'Returns a TOON-formatted table with one row per product. ' +
            'Optionally pass a "columns" array of column header names to restrict output to specific fields — ' +
            'defaults to all visible grid columns. productKeyDisplay and hasErrors are always included. ' +
            'Use column names from {{customAttributes}} and {{standardColumns}} for the columns parameter. ' +
            'Use this to inspect known products; to find products by attribute value use search-attribute-values or filter-column. ' +
            'The result will arrive as an automated [Tool] message — it was NOT entered by the user.',
          workingLabel: 'Fetching product details',
          confirmationLabel: args => `fetch details for ${(args.keys || []).length} product(s)`,
          parameters: {
            type: 'object',
            properties: {
              keys: {
                type: 'array',
                items: { type: 'string' },
                description: 'Array of exact productKeyDisplay values to look up.',
              },
              columns: {
                type: 'array',
                items: { type: 'string' },
                description:
                  'Optional. Column header names to include in the output (from {{customAttributes}} or {{standardColumns}}). ' +
                  'Omit to return all visible columns. productKeyDisplay and hasErrors are always included.',
              },
            },
            required: ['keys'],
          },
          handler: ({ keys: requestedKeys, columns }) => {
            if (!instance.proxy.productsData?.length) {
              return '[Tool] Product data is still loading. Please wait and try again.';
            }
            if (!instance.proxy.gridApi) {
              return '[Tool] Grid is not ready yet. Please wait and try again.';
            }

            const allProducts = instance.proxy.productsData;
            const requested = requestedKeys || [];
            const matched = [];
            const notFound = [];

            requested.forEach(key => {
              const product = allProducts.find(p => String(p.productKeyDisplay ?? '') === key);
              if (product) matched.push(product);
              else notFound.push(key);
            });

            if (!matched.length) {
              return `[Tool] No products found for the requested keys: ${notFound.map(k => `"${k}"`).join(', ')}.`;
            }

            const { fields, unrecognised } = resolveProductFields(columns);
            const encoded = buildProductToonTable(matched, fields);
            const capError = checkProductOutputCap(encoded, matched.length);
            if (capError) return capError;

            const parts = [
              `[Tool] Product details (${matched.length} found, ${notFound.length} not found):`,
              '```toon',
              encoded,
              '```',
            ];
            if (notFound.length) {
              parts.push(`Not found: ${notFound.map(k => `"${k}"`).join(', ')}`);
            }
            if (unrecognised.length) {
              parts.push(
                `Unrecognised columns ignored: ${unrecognised.map(c => `"${c}"`).join(', ')}`
              );
            }
            return parts.join('\n');
          },
        },

        'get-visible-products': {
          readOnly: true,
          description:
            'Return field values for every row currently visible in the grid (after all active filters). ' +
            'Use this after calling filter-column or set-search to discover which products are showing and inspect their data. ' +
            'For analysis questions (breakdowns, counts by attribute value, distributions, etc.), fetch the data with this tool ' +
            'and then present the results using render_table or render_chart. ' +
            'Optionally pass a "columns" array of column header names to restrict output to specific fields — ' +
            'defaults to all visible grid columns. productKeyDisplay and hasErrors are always included. ' +
            'Use column names from {{customAttributes}} and {{standardColumns}} for the columns parameter. ' +
            'Returns a TOON-formatted table; output is capped at about 20 k tokens — use "columns" or apply a more specific filter if the cap is hit. ' +
            'The result will arrive as an automated [Tool] message — it was NOT entered by the user.',
          workingLabel: 'Reading visible products',
          confirmationLabel: 'list visible products',
          parameters: {
            type: 'object',
            properties: {
              columns: {
                type: 'array',
                items: { type: 'string' },
                description:
                  'Optional. Column header names to include in the output (from {{customAttributes}} or {{standardColumns}}). ' +
                  'Omit to return all visible columns. productKeyDisplay and hasErrors are always included.',
              },
            },
            required: [],
          },
          handler: ({ columns } = {}) => {
            if (!instance.proxy.gridApi) {
              return '[Tool] Grid is not ready yet. Please wait and try again.';
            }

            const visible = [];
            instance.proxy.gridApi.forEachNodeAfterFilter(node => {
              if (node.data) visible.push(node.data);
            });

            if (visible.length === 0) {
              return '[Tool] No products are currently visible in the grid (all rows filtered out).';
            }

            const { fields, unrecognised } = resolveProductFields(columns);
            const encoded = buildProductToonTable(visible, fields);
            const capError = checkProductOutputCap(encoded, visible.length);
            if (capError) return capError;

            const parts = [
              `[Tool] Visible products (${visible.length}):`,
              '```toon',
              encoded,
              '```',
            ];
            if (unrecognised.length) {
              parts.push(
                `Unrecognised columns ignored: ${unrecognised.map(c => `"${c}"`).join(', ')}`
              );
            }
            return parts.join('\n');
          },
        },

        'get-product-errors': {
          readOnly: true,
          description:
            'Get a field-level breakdown of validation issues for a specific product. ' +
            'Returns structured JSON with field IDs, human-readable names, and messages. ' +
            'Each message carries a severity: "block" prevents saving, "error" must be fixed, "warn" is advisory. ' +
            'Only works for products that currently have issues. ' +
            'The result will arrive as an automated [Tool] message — it was NOT entered by the user.',
          workingLabel: 'Checking product errors',
          confirmationLabel: 'check product validation errors',
          parameters: {
            type: 'object',
            properties: {
              value: {
                type: 'string',
                description: 'The productKeyDisplay of the product to check.',
              },
            },
            required: ['value'],
          },
          handler: ({ value }) => {
            if (!instance.proxy.productsData?.length) {
              return '[Tool] Product data is still loading. Please wait and try again.';
            }

            const term = (value || '').trim().toLowerCase();
            const product = instance.proxy.productsData.find(
              p => String(p.productKeyDisplay ?? '').toLowerCase() === term
            );
            if (!product) return `[Tool] No product found with productKeyDisplay "${value}".`;

            const rowIssues = getRowIssues(product.productKey);
            if (!rowIssues) {
              return `[Tool] No errors found for product ${product.productKeyDisplay} (${product.itemDescription}).`;
            }

            const result = {
              productKeyDisplay: product.productKeyDisplay,
              itemDescription: product.itemDescription,
              errors: Object.entries(rowIssues).map(([field, cellEntry]) => ({
                field,
                name: instance.proxy.resolveFieldDisplayName(field),
                messages: flattenCellIssues(cellEntry),
              })),
            };
            return `[Tool] ${JSON.stringify(result, null, 2)}`;
          },
        },

        'summarize-validation-errors': {
          readOnly: true,
          description:
            'Get an aggregate summary of all validation errors across the scenario. Returns structured JSON ' +
            'with each entry containing: field (technical ID), name (human-readable label), errorType, severity, and productCount. ' +
            'Severity is one of "block" (prevents saving), "error" (must be fixed) or "warn" (advisory). ' +
            'Use name when explaining errors to the user; use field as a technical reference only. ' +
            'Optionally also filter the grid to show only error products by passing filterGrid: true. ' +
            'Use this to understand the scale and nature of problems before deciding how to help. ' +
            'The result will arrive as an automated [Tool] message — it was NOT entered by the user.',
          workingLabel: 'Checking errors',
          confirmationLabel: 'check validation error summary',
          parameters: {
            type: 'object',
            properties: {
              filterGrid: {
                type: 'boolean',
                description: 'If true, also filter the grid to show only products with errors.',
              },
            },
            required: [],
          },
          handler: ({ filterGrid = false } = {}) => {
            if (!instance.proxy.productsData?.length) {
              return '[Tool] Product data is still loading. Please wait and try again.';
            }
            const invalidRows = Object.values(tracker.state.invalidCells);
            if (!invalidRows.length) {
              return '[Tool] No products have validation errors.';
            }

            // Aggregate one entry per field + severity + message, with resolved display names
            const errorsByType = {};
            invalidRows.forEach(rowIssues => {
              each(rowIssues, (cellEntry, fieldName) => {
                flattenCellIssues(cellEntry).forEach(({ severity: severityClass, message }) => {
                  const key = `${fieldName}|${severityClass}|${message}`;
                  if (!errorsByType[key]) {
                    errorsByType[key] = {
                      field: fieldName,
                      name: instance.proxy.resolveFieldDisplayName(fieldName),
                      errorType: message,
                      severity: severityClass,
                      productCount: 0,
                    };
                  }
                  errorsByType[key].productCount += 1;
                });
              });
            });

            const fields = Object.values(errorsByType).sort(
              (a, b) => b.productCount - a.productCount
            );

            // Optionally filter the grid to error products
            let gridFilteredToErrors = instance.proxy.validationFilter === severity.error;
            if (filterGrid && !gridFilteredToErrors) {
              instance.proxy.onValidationFilterChange(severity.error);
              gridFilteredToErrors = true;
            }

            const result = {
              totalProductsWithErrors: invalidRows.length,
              gridFilteredToErrors,
              fields,
            };
            return `[Tool] ${JSON.stringify(result, null, 2)}`;
          },
        },

        'get-column-values': {
          readOnly: true,
          description:
            'Get all distinct values that exist in a specific column, so you can confirm the exact spelling and casing before calling filter-column. ' +
            'Use this when you know which column to filter on but are unsure of the exact value (e.g. is it "Choco", "CHOCO", or "Chocolate"?). ' +
            'Results are capped at 50 unique values. ' +
            'Works on both custom attribute columns and standard grid columns.',
          workingLabel: 'Fetching column values',
          confirmationLabel: args => `get unique values for "${args.columnName}"`,
          parameters: {
            type: 'object',
            properties: {
              columnName: {
                type: 'string',
                description:
                  'The exact column header name (from {{customAttributes}} or {{standardColumns}}).',
              },
            },
            required: ['columnName'],
          },
          handler: ({ columnName }) => {
            if (!instance.proxy.gridApi) {
              return '[Tool] Grid is not ready yet. Please wait and try again.';
            }
            if (!instance.proxy.productsData?.length) {
              return '[Tool] Product data is still loading. Please wait and try again.';
            }

            const nameLower = (columnName || '').toLowerCase().trim();
            const customAttrs = Object.values(instance.proxy.customAttributeColumnsByField || {});
            const customMatch = customAttrs.find(a => a.headerName?.toLowerCase() === nameLower);
            const allCols = instance.proxy.gridApi.getColumns() || [];
            let fieldId = null;

            if (customMatch) {
              fieldId = customMatch.field;
            } else {
              const matchedCol = allCols.find(
                col => col.getColDef().headerName?.toLowerCase() === nameLower
              );
              if (matchedCol) fieldId = matchedCol.getColId();
            }

            if (!fieldId) {
              return `[Tool] Column "${columnName}" not found. Check {{customAttributes}} and {{standardColumns}} for exact names.`;
            }

            const CAP = 50;
            const seen = new Set();
            instance.proxy.productsData.some(product => {
              const val = product[fieldId];
              if (val != null && val !== '') seen.add(String(val));
              return seen.size > CAP;
            });

            const uniqueValues = [...seen].sort((a, b) => a.localeCompare(b));
            if (!uniqueValues.length) {
              return `[Tool] Column "${columnName}" has no populated values.`;
            }

            const capped = uniqueValues.length === CAP;
            return `[Tool] Unique values for "${columnName}" (${uniqueValues.length}${capped ? '+' : ''}):\n${uniqueValues
              .map(v => `  • ${v}`)
              .join('\n')}${capped ? `\n  … (capped at ${CAP} — more may exist)` : ''}`;
          },
        },

        'search-attribute-values': {
          readOnly: true,
          description:
            'Search across all custom attribute columns for a term, to find which attribute(s) contain it and what the exact matching values are. ' +
            'Use this when the user refers to a value (e.g. "choco", "mini", "lait") without specifying a column and you do not know which custom attribute contains it. ' +
            'The search is case-insensitive and partial (substring match). ' +
            'If exactly ONE attribute contains exactly ONE distinct matching value, immediately call filter-column with that column and value — do not ask the user first. ' +
            'If there are multiple matching attributes or multiple matching values within one attribute, present the options and ask the user which to filter on.',
          workingLabel: 'Searching attribute values',
          confirmationLabel: args => `search custom attributes for "${args.value}"`,
          parameters: {
            type: 'object',
            properties: {
              value: {
                type: 'string',
                description:
                  'The term to search for across all custom attribute values (case-insensitive substring match).',
              },
            },
            required: ['value'],
          },
          handler: ({ value }) => {
            if (!instance.proxy.productsData?.length) {
              return '[Tool] Product data is still loading. Please wait and try again.';
            }

            const needle = (value || '').toLowerCase().trim();
            if (!needle) return '[Tool] No search term provided.';

            const customAttrs = Object.values(instance.proxy.currentCustomAttributes || {});
            if (!customAttrs.length) {
              return '[Tool] No custom attributes are defined for this scenario.';
            }

            const matches = [];
            customAttrs.forEach(attr => {
              const matchingValues = new Set();
              instance.proxy.productsData.forEach(product => {
                const val = product[attr.id];
                if (val != null && val !== '' && String(val).toLowerCase().includes(needle)) {
                  matchingValues.add(String(val));
                }
              });
              if (matchingValues.size > 0) {
                matches.push({ attrName: attr.name, values: matchingValues });
              }
            });

            if (!matches.length) {
              return `[Tool] No custom attribute values found containing "${value}".`;
            }

            // Single attribute, single distinct value → signal agent to proceed immediately
            if (matches.length === 1 && matches[0].values.size === 1) {
              const { attrName, values: vals } = matches[0];
              const exactValue = [...vals][0];
              return (
                '[Tool] SINGLE MATCH — call filter-column immediately with these parameters:\n' +
                `  Column: "${attrName}"\n` +
                `  Value:  "${exactValue}"`
              );
            }

            const lines = [`[Tool] Found "${value}" in the following custom attributes:\n`];
            matches.forEach(({ attrName, values: vals }) => {
              const sorted = [...vals].sort((a, b) => a.localeCompare(b));
              lines.push(`  ${attrName}: ${sorted.map(v => `"${v}"`).join(', ')}`);
            });
            lines.push('\nAsk the user which attribute and value to filter on.');
            return lines.join('\n');
          },
        },

        'get-attribute-stats': {
          readOnly: true,
          description:
            'Get population statistics for all custom attributes — how many products have a value vs. are blank. ' +
            'Use this to assess attribute completeness and identify gaps. ' +
            'The result will arrive as an automated [Tool] message — it was NOT entered by the user.',
          workingLabel: 'Checking attribute completeness',
          confirmationLabel: 'check attribute completeness',
          parameters: { type: 'object', properties: {}, required: [] },
          handler: () => {
            if (!instance.proxy.productsData?.length) {
              return '[Tool] Product data is still loading. Please wait and try again.';
            }

            const customAttrs = Object.values(instance.proxy.currentCustomAttributes || {});
            if (!customAttrs.length) {
              return '[Tool] No custom attributes are defined for this scenario.';
            }

            const total = instance.proxy.productsData.length;
            const savedValues = instance.proxy.savedValuesPerCustomAttribute || {};
            const lines = [`[Tool] Custom attribute population (${total} products total):`, ''];

            const displayAttrs = customAttrs.slice(0, 10);
            displayAttrs.forEach(attr => {
              const populated = instance.proxy.productsData.filter(
                p => p[attr.id] != null && p[attr.id] !== ''
              ).length;
              const pct = total > 0 ? Math.round((populated / total) * 100) : 0;

              lines.push(`${attr.name}: ${populated}/${total} populated (${pct}%)`);

              // Up to 5 example blank products
              const blanks = instance.proxy.productsData
                .filter(p => p[attr.id] == null || p[attr.id] === '')
                .slice(0, 5)
                .map(p => p.productKeyDisplay);
              if (blanks.length) {
                lines.push(`  Blank examples: ${blanks.join(', ')}`);
              }

              // Known values hint from saved state
              const knownVals = savedValues[attr.id]?.forExistingProducts;
              if (knownVals?.length) {
                const display = knownVals.slice(0, 8).join(', ');
                lines.push(`  Known values: ${display}${knownVals.length > 8 ? ', …' : ''}`);
              }

              lines.push('');
            });

            if (customAttrs.length > 10) {
              lines.push(`… and ${customAttrs.length - 10} more attribute(s) not shown.`);
            }

            return lines.join('\n');
          },
        },

        // ─── EDIT ────────────────────────────────────────────────────────
        'apply-product-updates': {
          description:
            'Apply product field updates from a TOON-formatted table — the same format returned by get-product-by-key and get-visible-products. ' +
            'Use this for any combination of products and fields: one product, hundreds of products, one column, or many columns. ' +
            'Construct a TOON table with productKeyDisplay as one of the columns, plus any fields to update as additional columns. ' +
            'Example: products[2]{productKeyDisplay,margin,Brand Tier}:\n  SKU-001,0.35,Premium\n  SKU-002,0.42,Economy ' +
            'The prefix before [N]{...}: (e.g. "products", "updates") is ignored — any prefix is accepted. ' +
            'Excluded fields (not editable): storeClassEligibility, parentProduct, isNewProduct, fromTemplate, deleteNewProduct, dependentSource. ' +
            'For percentage fields (margin, theoreticalMargin, shrinkToSalesRatio), use decimal scale (e.g. 0.5 for 50%) — the same scale returned by get-product-by-key. ' +
            'For maintainOption, use one of: margin, marginRate, price, null (for doNothing), manuallySet. ' +
            'Use bulk-set-visible-column instead when you want to target all currently visible (filtered) products without enumerating keys. ' +
            'Do not confirm success in your message text; the system will confirm or report errors.',
          workingLabel: 'Updating products',
          confirmationLabel: () => 'Apply product updates',
          parameters: {
            type: 'object',
            properties: {
              toon: {
                type: 'string',
                description:
                  'A TOON-formatted table string. Must include a productKeyDisplay column. ' +
                  'Header: anyPrefix[N]{productKeyDisplay,field1,field2,...}: followed by one indented row per product. ' +
                  'CSV-escape values containing commas or double quotes.',
              },
            },
            required: ['toon'],
          },
          handler: ({ toon }) => {
            if (!canEditAttributeEditorPage.value) {
              return '[Tool] Action failed: you do not have permission to edit this page.';
            }
            if (!instance.proxy.productsData?.length) {
              return '[Tool] Product data is still loading. Please wait and try again.';
            }

            const parsed = parseToonTable(toon);
            if (parsed.error) {
              return `[Tool] Action failed: could not parse TOON input — ${parsed.error}`;
            }

            const { columns, rows } = parsed;
            const keyColIndex = columns.findIndex(c => c.toLowerCase() === 'productkeydisplay');
            if (keyColIndex === -1) {
              return '[Tool] Action failed: TOON table must include a productKeyDisplay column.';
            }
            const updateCols = columns
              .map((name, index) => ({ name, index }))
              .filter(({ index }) => index !== keyColIndex);
            if (!updateCols.length) {
              return '[Tool] Action failed: TOON table must include at least one field column besides productKeyDisplay.';
            }

            const allProducts = instance.proxy.productsData;
            const updateBatch = [];
            const resultLines = [];

            rows.forEach(row => {
              const pKey = row[keyColIndex] ?? '';
              if (!pKey) {
                resultLines.push('  • SKIP: row has empty productKeyDisplay');
                return;
              }
              const product = allProducts.find(p => String(p.productKeyDisplay ?? '') === pKey);
              if (!product) {
                resultLines.push(`  • ${pKey}: not found`);
                return;
              }

              const updateObj = { productKey: Number(product.productKey) };
              const fieldLines = [];
              updateCols.forEach(({ name, index }) => {
                const rawValue = row[index] ?? '';
                const outcome = coerceProductFieldUpdate(name, rawValue);
                if (outcome.error) {
                  fieldLines.push(`    - ${outcome.error}`);
                } else {
                  updateObj[outcome.fieldId] = outcome.coercedValue;
                  fieldLines.push(`    - ${name} → ${outcome.coercedValue} ✓`);
                }
              });

              const fieldsUpdated = Object.keys(updateObj).length - 1;
              if (fieldsUpdated > 0) {
                updateBatch.push(updateObj);
                resultLines.push(
                  `  • ${pKey} (${product.itemDescription}): ${fieldsUpdated} field(s) queued`
                );
              } else {
                resultLines.push(`  • ${pKey}: no valid fields to update`);
              }
              resultLines.push(...fieldLines);
            });

            if (!updateBatch.length) {
              return `[Tool] Action failed: no valid updates to apply.\n${resultLines.join('\n')}`;
            }

            instance.proxy.updateRows(updateBatch);
            return [
              `[Tool] Updated ${updateBatch.length} product(s) across ${updateCols.length} field(s):`,
              '',
              ...resultLines,
              '',
              'Changes are unsaved.',
            ].join('\n');
          },
        },

        'bulk-set-visible-column': {
          description:
            'Set a SINGLE field to the SAME value for ALL products currently visible in the grid (after all active filters). ' +
            'Use this when the user wants to apply one uniform value to every visible row — e.g. set Brand Tier to Premium for all filtered products. ' +
            'For editing specific products by key with full column control, use apply-product-updates instead. ' +
            'IMPORTANT: before calling this action, always confirm the active filter state with the user — ' +
            'quote the current filters and product count so they can verify the scope is correct. ' +
            'Excluded fields (not editable via this action): storeClassEligibility, parentProduct, isNewProduct, fromTemplate, ' +
            'deleteNewProduct, dependentSource, recentlySold, inReset, originalMargin, originalPrice, ' +
            'originalShrinkToSalesRatio, originalPurchasePrice, originalTransferPrice. ' +
            'For percentage fields (margin, theoreticalMargin, shrinkToSalesRatio), pass the value in decimal scale ' +
            '(e.g. 0.5 for 50%) — identical to how get-product-by-key returns them. ' +
            'For maintainOption, use one of: margin, marginRate, price, null (for doNothing), manuallySet. ' +
            'All values should be strings; the system will coerce types automatically. ' +
            'Do not confirm success in your message text; the system will confirm or report errors.',
          workingLabel: 'Updating filtered products',
          confirmationLabel: ({ field, value }) => {
            let count = 0;
            instance.proxy.gridApi?.forEachNodeAfterFilter(() => {
              count += 1;
            });
            return `set '${field}' to '${value}' for ${count} product(s)`;
          },
          parameters: {
            type: 'object',
            properties: {
              field: {
                type: 'string',
                description:
                  'The field name to update (standard field key or custom attribute id).',
              },
              value: {
                type: 'string',
                description: 'The new value to apply to all filtered products.',
              },
            },
            required: ['field', 'value'],
          },
          handler: ({ field, value }) => {
            if (!canEditAttributeEditorPage.value) {
              return '[Tool] Action failed: you do not have permission to edit this page.';
            }
            if (!instance.proxy.productsData?.length) {
              return '[Tool] Product data is still loading. Please wait and try again.';
            }

            const excludedFields = new Set([
              'storeClassEligibility',
              'parentProduct',
              'isNewProduct',
              'fromTemplate',
              'deleteNewProduct',
              'dependentSource',
              'recentlySold',
              'inReset',
              'originalMargin',
              'originalPrice',
              'originalShrinkToSalesRatio',
              'originalPurchasePrice',
              'originalTransferPrice',
            ]);

            if (excludedFields.has(field)) {
              return `[Tool] Action failed: '${field}' cannot be edited via this action.`;
            }

            const booleanFields = new Set(['assortment', 'inReset', 'recentlySold']);
            const integerFields = new Set(['palletContent', 'periodID', 'contentValue']);
            const validStandardFields = new Set(Object.values(fieldEnum));
            const customAttrFields = instance.proxy.customAttributeColumnsByField || {};

            // Resolve display name → internal field ID for custom attributes (case-insensitive fallback)
            const customAttrByDisplayName = Object.values(customAttrFields).find(
              col => col.headerName?.toLowerCase() === field.toLowerCase()
            );
            if (customAttrByDisplayName && !customAttrFields[field])
              field = customAttrByDisplayName.field;

            const isStandardField = validStandardFields.has(field);
            const isCustomField = !!customAttrFields[field];

            if (!isStandardField && !isCustomField) {
              return `[Tool] Action failed: '${field}' is not a recognised field or custom attribute id.`;
            }

            let coercedValue;
            if (field === 'maintainOption') {
              if (value === 'null' || value === '') {
                coercedValue = null;
              } else {
                const validOptions = Object.values(maintainOptions).filter(v => v !== null);
                if (!validOptions.includes(value)) {
                  return `[Tool] Action failed: invalid maintainOption value "${value}". Valid options: ${validOptions.join(', ')}, null`;
                }
                coercedValue = value;
              }
            } else if (booleanFields.has(field)) {
              coercedValue = value === 'true' || value === true;
            } else if (integerFields.has(field)) {
              coercedValue = parseInt(value, 10);
              if (Number.isNaN(coercedValue)) {
                return `[Tool] Action failed: "${value}" is not a valid integer for field '${field}'.`;
              }
            } else if (isCustomField) {
              coercedValue = value;
            } else {
              const asFloat = parseFloat(value);
              coercedValue = Number.isNaN(asFloat) ? value : asFloat;
            }

            const updates = [];
            instance.proxy.gridApi?.forEachNodeAfterFilter(node => {
              if (node.data?.productKey != null) {
                updates.push({ productKey: Number(node.data.productKey), [field]: coercedValue });
              }
            });

            if (!updates.length) {
              return '[Tool] Action failed: no products are currently visible in the grid. Apply filters before using bulk-set-visible-column.';
            }

            instance.proxy.updateRows(updates);
            return `[Tool] Set '${field}' to '${coercedValue}' for ${updates.length} product(s). Changes are unsaved.`;
          },
        },

        // ─── FILTER / NAVIGATION ─────────────────────────────────────────
        'show-error-products': {
          description:
            'Filter the grid to show only products with validation errors. ' +
            'Use summarize-validation-errors first to understand the nature and scale of errors. ' +
            'Do not confirm in your message text; the system will confirm.',
          workingLabel: 'Filtering to error products',
          confirmationLabel: 'filter grid to show only error products',
          parameters: { type: 'object', properties: {}, required: [] },
          handler: () => {
            const count = tracker.countRowsWithFailedValidations(severity.error);
            if (!count) return '[Tool] No products have validation errors.';
            if (instance.proxy.validationFilter === severity.error) {
              return `[Tool] Already showing only products with errors (${count} product(s)).`;
            }
            instance.proxy.onValidationFilterChange(severity.error);
            return `[Tool] Now showing only products with errors (${count} product(s)). Use get-products to see them.`;
          },
        },

        'clear-filters': {
          description:
            'Clear all active grid filters, including column filters and the search bar.',
          readOnly: true,
          workingLabel: 'Clearing filters',
          parameters: { type: 'object', properties: {}, required: [] },
          handler: () => {
            // clearFilters() handles searchString reset, filterModel, and onFilterChanged internally
            instance.proxy.clearFilters();
            const total = instance.proxy.productsData?.length || 0;
            return `[Tool] All filters cleared. ${total} product(s) now visible.`;
          },
        },

        'set-search': {
          description:
            'Set the grid search bar to a specific term — this filters what the user sees in the grid. ' +
            'Only use this when the user explicitly wants to visually filter the grid. ' +
            'Do NOT use this just to look up product data — use get-product-by-key for that instead (it never changes the grid). ' +
            'Search combines with any active validation filter.',
          readOnly: true,
          workingLabel: 'Applying search',
          confirmationLabel: args => `set grid search to "${args.value}"`,
          parameters: {
            type: 'object',
            properties: {
              value: { type: 'string', description: 'The search term to apply to the grid.' },
            },
            required: ['value'],
          },
          handler: ({ value }) => {
            instance.proxy.searchString = value || '';
            instance.proxy.gridApi?.onFilterChanged();
            const visible = [];
            instance.proxy.gridApi?.forEachNodeAfterFilter(node => visible.push(node));
            return `[Tool] Grid search set to "${value}". ${visible.length} product(s) now visible.`;
          },
        },

        'filter-column': {
          description:
            'Filter the grid by one or more column values. ' +
            'Pass an array of { columnName, value, operator? } objects where columnName is the column header exactly as shown in the grid. ' +
            'operator defaults to "=" (equals). Supported operators: =, !=, >, >=, <, <=. ' +
            'Comparison operators (>, >=, <, <=, !=) are only valid on numeric columns — they will be rejected on text or attribute columns. ' +
            'Numeric values must be at display scale (e.g. 50 for 50% margin, not 0.5). ' +
            'This REPLACES all existing column filters — the search bar and error-only filters are unaffected. ' +
            'Use this whenever the user asks to see or count products by column value or range, ' +
            'e.g. "how many products have Product Type = Mini", "show products with price > 3", "margin >= 30". ' +
            'Do NOT use set-search for attribute/column value queries — use this tool instead. ' +
            'To express multiple conditions in one call, pass multiple entries in the filters array. ' +
            'Boolean columns (e.g. Assortment, Recently Sold) are not supported — direct the user to the column header filter menu. ' +
            'IMPORTANT: avoid filtering on Item Description unless the user explicitly names that column. ' +
            'If the user gives a value without specifying a column, call search-attribute-values first to find which custom attribute contains it. ' +
            'If you know the column but are unsure of the exact value spelling or casing, call get-column-values first. ' +
            'Never assume a value — always verify before filtering. ' +
            'MANDATORY: for any custom attribute, especially yes/no-style questions (Bio, Fairtrade, Organic, etc.), ' +
            'you MUST call get-column-values first — stored values could be "OUI", "X", "✓", "1" or anything else. ' +
            'Never guess — a wrong value will silently return 0 results.',
          readOnly: true,
          workingLabel: 'Filtering grid',
          confirmationLabel: args => {
            const desc = (args.filters || [])
              .map(f => `${f.columnName} ${f.operator || '='} "${f.value}"`)
              .join(' and ');
            return `filter the grid where ${desc}`;
          },
          parameters: {
            type: 'object',
            properties: {
              filters: {
                type: 'array',
                description:
                  'One or more column filters to apply. Replaces all existing column filters.',
                items: {
                  type: 'object',
                  properties: {
                    columnName: {
                      type: 'string',
                      description: 'The column header name exactly as shown in the grid.',
                    },
                    value: {
                      type: 'string',
                      description: 'The value to filter for, at display scale for numeric columns.',
                    },
                    operator: {
                      type: 'string',
                      enum: ['=', '!=', '>', '>=', '<', '<='],
                      description:
                        'Comparison operator. Defaults to "=". Comparison operators other than "=" are only valid on numeric columns.',
                    },
                  },
                  required: ['columnName', 'value'],
                },
              },
            },
            required: ['filters'],
          },
          handler: ({ filters }) => {
            if (!instance.proxy.gridApi) {
              return '[Tool] Action failed: grid is not ready.';
            }
            if (!filters?.length) {
              return '[Tool] Action failed: at least one filter entry is required.';
            }

            // Map operator symbols to AG Grid number filter type strings
            const numericOperatorMap = {
              '=': 'equals',
              '!=': 'notEqual',
              '>': 'greaterThan',
              '>=': 'greaterThanOrEqual',
              '<': 'lessThan',
              '<=': 'lessThanOrEqual',
            };

            const filterModel = {};
            const applied = [];
            const skipped = [];

            filters.forEach(({ columnName, value, operator = '=' }) => {
              const nameLower = (columnName || '').trim().toLowerCase();
              if (!nameLower) {
                skipped.push('(empty column name)');
                return;
              }

              // 1. Search custom attributes by headerName
              const customAttrs = Object.values(instance.proxy.customAttributeColumnsByField || {});
              const customMatch = customAttrs.find(a => a.headerName?.toLowerCase() === nameLower);
              let fieldId = null;
              let agCol = null;

              const allCols = instance.proxy.gridApi.getColumns() || [];

              if (customMatch) {
                fieldId = customMatch.field; // field === colId for custom attrs
                agCol = allCols.find(c => c.getColId() === fieldId) || null;
              } else {
                // 2. Search all standard columns by their colDef headerName
                const matchedCol = allCols.find(col => {
                  const colDef = col.getColDef();
                  return colDef.headerName && colDef.headerName.toLowerCase() === nameLower;
                });
                if (matchedCol) {
                  fieldId = matchedCol.getColId(); // always use colId as setFilterModel key
                  agCol = matchedCol;
                }
              }

              if (!fieldId || !agCol) {
                skipped.push(`"${columnName}" — column not found`);
                return;
              }

              // Refuse columns that are currently hidden (hide: true in column state)
              const colState = instance.proxy.gridApi
                .getColumnState()
                .find(cs => cs.colId === fieldId);
              if (colState?.hide === true) {
                skipped.push(`"${columnName}" — column is currently hidden in the grid`);
                return;
              }

              const colDef = agCol.getColDef();

              // Refuse columns with filtering explicitly disabled
              if (colDef.filter === false) {
                skipped.push(`"${columnName}" — column does not support filtering`);
                return;
              }

              const isNumeric =
                colDef.filter === 'agNumberColumnFilter' || colDef.type === 'numericColumnNew';
              const isBoolean = colDef.type === 'booleanColumnCustom';
              // Only columns with an explicit 'agTextColumnFilter' use the text format.
              // Everything else (filter: true, filter: 'agSetColumnFilter', or unset) uses
              // agSetColumnFilter in AG Grid Enterprise — including all custom attribute columns.
              const isExplicitTextFilter = colDef.filter === 'agTextColumnFilter';

              if (isBoolean) {
                skipped.push(
                  `"${columnName}" — boolean columns cannot be filtered via this tool; direct the user to the column header filter menu`
                );
                return;
              }

              if (isNumeric) {
                const numVal = parseFloat(value);
                if (Number.isNaN(numVal)) {
                  skipped.push(
                    `"${columnName}" — "${value}" is not a valid number for a numeric column`
                  );
                  return;
                }
                const agType = numericOperatorMap[operator];
                if (!agType) {
                  skipped.push(
                    `"${columnName}" — unknown operator "${operator}"; use one of: =, !=, >, >=, <, <=`
                  );
                  return;
                }
                filterModel[fieldId] = { filterType: 'number', type: agType, filter: numVal };
              } else {
                // Text and set columns only support equals (=)
                if (operator !== '=') {
                  skipped.push(
                    `"${columnName}" — operator "${operator}" is only valid on numeric columns; use = for text/attribute columns`
                  );
                  return;
                }
                if (isExplicitTextFilter) {
                  filterModel[fieldId] = { filterType: 'text', type: 'contains', filter: value };
                } else {
                  // agSetColumnFilter (Enterprise default for filter: true)
                  filterModel[fieldId] = { filterType: 'set', values: [value] };
                }
              }

              applied.push(`"${columnName}" ${operator} "${value}"`);
            });

            if (!Object.keys(filterModel).length) {
              const skipDetail = skipped.length ? `\nSkipped: ${skipped.join('; ')}` : '';
              return `[Tool] Action failed: no valid column filters could be applied.${skipDetail}`;
            }

            // Apply the whole filter model in one call (same API as clearFilters uses).
            instance.proxy.gridApi.setFilterModel(filterModel);
            instance.proxy.gridApi.onFilterChanged();

            const visible = [];
            instance.proxy.gridApi.forEachNodeAfterFilter(node => visible.push(node));

            const lines = [
              `[Tool] Column filter applied (${applied.join(', ')}). ${visible.length} product(s) visible.`,
            ];
            if (skipped.length) {
              lines.push(`Skipped: ${skipped.join('; ')}`);
            }
            if (instance.proxy.validationFilter) {
              lines.push(
                'Note: the grid also has a validation filter applied — the count above reflects both filters combined.'
              );
            }
            return lines.join('\n');
          },
        },

        // ─── ATTRIBUTE MANAGEMENT ─────────────────────────────────────────
        'delete-custom-attribute': {
          description:
            'Delete a custom attribute column from the scenario. ' +
            'Do not confirm in your message text; the system will confirm.',
          workingLabel: 'Deleting attribute',
          confirmationLabel: args => `delete custom attribute "${args.value}"`,
          parameters: {
            type: 'object',
            properties: {
              value: {
                type: 'string',
                description: 'The exact name of the custom attribute to delete.',
              },
            },
            required: ['value'],
          },
          handler: async ({ value }) => {
            if (!canEditAttributeEditorPage.value) {
              return '[Tool] Action failed: you do not have permission to edit this page.';
            }
            const nameLower = (value || '').trim().toLowerCase();
            const customAttrs = Object.values(instance.proxy.currentCustomAttributes || {});
            const match = customAttrs.find(a => a.name.toLowerCase() === nameLower);
            if (!match) {
              const available = customAttrs.map(a => a.name).join(', ') || 'none';
              return `[Tool] Action failed: no custom attribute named "${value}". Available: ${available}.`;
            }
            await instance.proxy.deleteAttribute(match.id);
            return `[Tool] Custom attribute "${match.name}" deleted. Save to make permanent.`;
          },
        },

        // ─── SAVE / DISCARD ──────────────────────────────────────────────
        'save-changes': {
          description:
            'Save all unsaved changes to the scenario. ' +
            'Only use when the user explicitly asks to save. ' +
            'If a dependency warning dialog appears, tell the user to review and action it in the UI. ' +
            'Do not confirm in your message text; the system will confirm or report errors.',
          workingLabel: 'Saving changes',
          confirmationLabel: 'save all unsaved changes',
          parameters: { type: 'object', properties: {}, required: [] },
          handler: async () => {
            if (!canEditAttributeEditorPage.value) {
              return '[Tool] Action failed: you do not have permission to edit this page.';
            }
            if (!instance.proxy.hasDataChanges) {
              return '[Tool] No unsaved changes to save.';
            }
            await instance.proxy.saveChanges();
            if (instance.proxy.dependencyTreeModalOpen) {
              return (
                '[Tool] Save triggered, but a dependency warning dialog has appeared. ' +
                'Please review the dialog in the UI and confirm or dismiss it before continuing.'
              );
            }
            return '[Tool] Changes saved successfully.';
          },
        },

        'discard-changes': {
          description:
            'Discard all unsaved changes and revert the scenario to its last saved state. ' +
            'IMPORTANT: this action bypasses the normal UI confirmation dialog. ' +
            'You MUST ask for explicit user confirmation before calling this — they must say yes or confirm. ' +
            'Only call this with confirmed=true after receiving explicit user confirmation. ' +
            'If unsure, ask the user to confirm first.',
          workingLabel: 'Discarding changes',
          confirmationLabel: 'discard all unsaved changes',
          parameters: {
            type: 'object',
            properties: {
              confirmed: {
                type: 'boolean',
                description:
                  'Must be true. Only set to true after explicit user confirmation to discard all changes.',
              },
            },
            required: ['confirmed'],
          },
          handler: async ({ confirmed = false }) => {
            if (!canEditAttributeEditorPage.value) {
              return '[Tool] Action failed: you do not have permission to edit this page.';
            }
            if (!confirmed) {
              return (
                '[Tool] Action refused: discard requires confirmed=true. ' +
                'Ask the user to confirm they want to discard all changes first.'
              );
            }
            if (!instance.proxy.hasDataChanges) {
              return '[Tool] No unsaved changes to discard.';
            }
            await instance.proxy.discard();
            return '[Tool] All unsaved changes have been discarded.';
          },
        },
      },
    });

    const { isDateValid } = useDayjs();

    const {
      isUnsavedDataModalOpen,
      closeUnsavedDataModal,
      confirmUnsavedDataModal,
      beforeRouteLeave,
    } = useUnsavedDataWarning();

    const { isLessThan, isGreaterThan } = useInputValidations();

    const { t, tkey } = useInternationalization(localizationKey);

    const { formatNumber, formatMargin, getRoundedPercentage } = useFormatNumber();
    const { getProductType, getProductTypeDisplayString } = useProductIndicators();

    return {
      // permissions
      canEditAttributeEditorPage,
      canEditClientID,
      templatesEnabled,
      // features
      betaClientIDEditEnabled,
      hasProductsExistingFromNewInTemplateEnabled,
      hasClientProductKeyColumnEnabled,
      gridContextMenusEnabled,
      dependentProductsEnabled,
      aiPanelEnabled,
      formatScenarioProgress,
      // unsavedDataWarning
      isUnsavedDataModalOpen,
      closeUnsavedDataModal,
      confirmUnsavedDataModal,
      beforeRouteLeave,
      // date utils
      isDateValid,
      // validations
      isLessThan,
      isGreaterThan,
      // i18n
      t,
      tkey,
      // formatters
      formatNumber,
      formatMargin,
      getRoundedPercentage,
      getProductType,
      getProductTypeDisplayString,
    };
  },

  data() {
    return {
      tracker,
      isGridLoading: false, // controls grid overlay
      // Unsubscribe handle for the shell's provided-components snapshot; null standalone.
      stopComponentsSubscription: null,
      savedValuesPerCustomAttribute: {},

      dependencyTreeModalOpen: false,
      dependencyTreeFeedback: {},
      dependencyTreeWarningMessage: null,

      fieldEnum,
      optionalFieldEnum: OptionalAttributeEditorAttributesEnums.optionalAttributeEditorAttributes,

      commonProductCategoryLevelKey: null,

      // dialogs
      isDeleteNewProductsDialogOpen: false,

      // ag-grid
      groupingColumn: {},
      groupingValues: [],
      groupingModalOpen: false,
      isRestOfMarketProductsModalOpen: false,
      isAddOptionalColumnsModalOpen: false,
      isResetDataModalOpen: false,
      isCleanAttributesModalOpen: false,
      size,
      customAttributeName: null,
      usedAttributeHeaders: [],
      validationFilter: null,
      validationFilterRowKeys: new Set(),
      blockingErrorExists: false,
      collapsedColGroup: false,
      gridOptions: {
        columnMenu: 'legacy',
        cellSelection: {
          handle: {
            mode: 'fill',
            // Override what happens when clicking and dragging a cell.
            setFillValue: params => this.getFillHandleValue(params),
          },
        },
        getMainMenuItems: this.getColumnMenuItems,
        stopEditingWhenCellsLoseFocus: true,
        defaultColGroupDef: { marryChildren: true },
        suppressContextMenu: !this.gridContextMenusEnabled,
        suppressDragLeaveHidesColumns: true,
        defaultColDef: {
          resizable: true,
          filter: true,
          sortable: true,
          wrapHeaderText: true,
          autoHeaderHeight: true,
          minWidth: 80,
          comparator: agGridUtils.sortings.naturalSort,
          menuTabs: ['filterMenuTab', 'generalMenuTab'],
          editable: () => !this.isEditingDisabled,
          suppressMovable: false,
          lockPinned: true,
          context: { required: true },
          headerClass: params => {
            const col = params.column;
            const groupCols = get(col, 'parent.displayedChildren');
            if (!groupCols && groupCols.length === 0) return null;
            return col.colId === head(groupCols).colId ? 'divider-left' : null;
          },
          cellClassRules: {
            ...validationCellClassRules,
            'diff-background': tracker.hasDiff,
            'divider-left': params => {
              const col = params.column;
              const groupCols = get(col, 'parent.displayedChildren');
              if (!groupCols && groupCols.length === 0) return false;
              return col.colId === head(groupCols).colId;
            },
          },
          valueParser: agGridUtils.parsers.defaultParser,
          tooltipValueGetter: params =>
            tracker.getCellTooltip(params, {
              formatIssue: issue => {
                const translationPath = `scenarioInputPage.attributes.validation.${issue.message}`;
                const translated = this.t(translationPath, issue.params);
                return translated !== translationPath ? translated : issue.message;
              },
              formatPreviousValue: displayValue => {
                const previousValue =
                  isNil(displayValue) || displayValue === ''
                    ? this.tkey('attributes.tooltips.emptyValue')
                    : String(displayValue);
                return this.tkey('attributes.tooltips.previousValue', { value: previousValue });
              },
            }),
        },
        processDataFromClipboard: agGridUtils.utils.processDataFromClipboard,
        processCellFromClipboard: agGridUtils.utils.processCellFromClipboard,
        // AG Grid v33 copies the formatted (translated) display value to clipboard by default.
        // For maintainOption we copy the raw enum value instead, so that valueParser receives
        // a value it can handle. All other columns use AG Grid's built-in formatValue.
        processCellForClipboard: params =>
          params.column.colId === fieldEnum.maintainOption
            ? params.value
            : params.formatValue(params.value),
        tooltipShowDelay: 0,
        isExternalFilterPresent() {
          return true;
        },
        getRowId(row) {
          const { data } = row;
          return String(data.productKey);
        },
        onColumnResized: () => {
          // Make sure all columns are resized and registered in grid on resize
          const index = this.headers.findIndex(h => h.groupId === 'custom-attributes');
          const customAttrsGroup = this.headers[index];
          const usedSources = uniq(compact(map(customAttrsGroup.children, 'source')));
          const sources = [
            uploadMethods.CSV,
            uploadMethods.FEED,
            uploadMethods.SCENARIO,
            uploadMethods.CUSTOM,
          ];
          if (intersectionWith(usedSources, sources, isEqual).length) this.gridApi.redrawRows();
        },
        rowHeight: 30,
        columnTypes: {
          numericColumnNew: {
            // AOV3-1423 TODO: move cellClassRules to agGridUtils.columnTypes.numericColumnCustom ?
            cellClassRules: {
              ...validationCellClassRules,
              'diff-background': tracker.hasDiff,
            },
          },
          booleanColumnCustom: agGridUtils.columnTypes.booleanColumnCustom,
        },
        statusBar: {
          statusPanels: [
            { statusPanel: 'agTotalRowCountComponent', align: 'left' },
            { statusPanel: 'agFilteredRowCountComponent', align: 'left' },
            { statusPanel: 'agSelectedRowCountComponent', align: 'left' },
            {
              statusPanel: 'agAggregationComponent',
              statusPanelParams: { aggFuncs: ['count'] },
              align: 'left',
            },
          ],
        },
        onFilterChanged: () => {
          this.filterModel = this.gridApi.getFilterModel();
        },
      },
      gridApi: null,
      columnDefs: null,
      searchString: '',
      headers: [],

      keyIdMap: {},
      rules: [
        value => this.isLessThan(value, maximumRowsToAdd),
        value => this.isGreaterThan(value, 0),
        value => isEmpty(value) || this.isInteger(value),
      ],
      filter: 'all_active',
      filters: [{ text: this.tkey('filter_all_active'), value: 'all_active' }],
      productsData: null,
      gridRowData: null,
      newProductRowsNumber: '',
      currentScenarioStateDiff: {}, // diffs against scenario
      // new attributes which came from csv, used in computed props
      newAttributes: [],
      updatedAttributes: {},
      attributesToClean: new Set(),
      savedAttributes: [],
      storeClassEligibilityFields: [],
      storeClassEligibilityHeaders: [],
      productsToDelete: [],

      isRenameCustomAttributeDialogOpen: false,
      customAttributeIdToRename: null,
      renamedCustomAttributeName: '',

      csvUploadLegends: {
        buttonName: this.$t('actions.addAttr'),
        title: this.tkey('attributes.addAttributesTitle'),
      },
      uploadedDataDetails: null,
      dataToUploadFromSource: {
        source: null,
        attributes: [],
      },
      optionalSchemaFields: new Set(),
      fieldsToIgnore: new Set(),

      deletedAttributes: [],
      deletedProducts: [],
      addedProducts: [],

      productsWithoutEligibility: [],

      isResetFromTemplate: false,
      productsWithoutPurchasePriceInformation: [],

      isSyncingProductData: false,

      extraValidations: {},
      filterModel: null,

      backupAdditionalFields: {},
    };
  },

  computed: {
    ...mapState(useContextStore, [
      'weeksNotSoldForDiscontinued',
      'clientConfig',
      'getCsvExport',
      'getDateFormats',
      'getClientConfig',
      'getCurrentNumericLocale',
      'getCurrentLocale',
      'getProductMinimumFacings',
      'getExcelDateFormat',
    ]),
    ...mapState(useFurnitureStore, ['scenarioFurniture']),
    ...mapState(useScenariosStore, ['selectedScenario']),
    ...mapState(useScenarioProductsStore, ['loading']),
    ...mapState(useWorkpackagesStore, [
      'selectedWorkpackage',
      'isSimpleSwapsWP',
      'workpackageProducts',
    ]),
    ...mapState(useToolDataStore, ['productHierarchy']),
    ...mapState(useAiStore, ['isSidePanelOpen']),

    isFreshWP() {
      return this.selectedWorkpackage.type === WorkpackageType.ASSORTMENT_FRESH;
    },

    hasCleanAttributesUpperCaseEnabled() {
      return get(this.getClientConfig, 'features.cleanAttributesUpperCaseEnabled', false);
    },

    showResetFromTemplateButton() {
      return this.templatesEnabled && !!this.selectedWorkpackage.templateId;
    },

    currentNumericLocale() {
      return this.getCurrentNumericLocale;
    },

    currentLocale() {
      return this.getCurrentLocale;
    },

    optionalAttributes() {
      if (this.hasAdditionalFieldsEnabled) {
        return [
          {
            field: 'seasonalWeekNumbers',
            name: `${this.tkey('attributes.seasonalWeekNumbers')}`,
            value: get(
              this.selectedScenario,
              'optionalAttributes.additionalFields.seasonalWeekNumbers'
            ),
          },
          {
            field: 'userEnteredSisterProduct',
            name: `${this.tkey('attributes.userEnteredSisterProduct')}`,
            value: get(
              this.selectedScenario,
              'optionalAttributes.additionalFields.userEnteredSisterProduct'
            ),
          },
          {
            field: 'seasonalProduct',
            name: `${this.tkey('attributes.seasonalProduct')}`,
            value: get(
              this.selectedScenario,
              'optionalAttributes.additionalFields.seasonalProduct'
            ),
          },
          {
            field: 'pgMustFlag',
            name: `${this.tkey('attributes.pgMustFlag')}`,
            value: get(this.selectedScenario, 'optionalAttributes.additionalFields.pgMustFlag'),
          },
          {
            field: 'minimumDistribution',
            name: `${this.tkey('attributes.minimumDistributionWoPercentage')}`,
            value: get(
              this.selectedScenario,
              'optionalAttributes.additionalFields.minimumDistribution'
            ),
          },
          {
            field: 'maximumDistribution',
            name: `${this.tkey('attributes.maximumDistribution')}`,
            value: get(
              this.selectedScenario,
              'optionalAttributes.additionalFields.maximumDistribution'
            ),
          },
          {
            field: 'localProduct',
            name: `${this.tkey('attributes.localProduct')}`,
            value: get(this.selectedScenario, 'optionalAttributes.additionalFields.localProduct'),
          },
        ];
      }
      return [
        {
          field: 'minimumFacings',
          name: `${this.tkey('attributes.minimumFacings')}`,
          value: get(this.selectedScenario, 'optionalAttributes.useMinimumFacings'),
        },
        {
          field: 'minimumDistribution',
          name: `${this.tkey('attributes.minimumDistribution')}`,
          value: get(this.selectedScenario, 'optionalAttributes.useMinimumDistribution'),
        },
      ];
    },

    transactionWeightDisabled() {
      const transactionWeightEnabled = get(
        this.clientConfig,
        'features.transactionWeightEnabled',
        false
      );
      return !transactionWeightEnabled;
    },

    initialHeaders() {
      const columns = {
        pinnedHeaders: {
          openByDefault: true,
          groupId: 'product-details',
          marryChildren: true,
          groupLockGroupColumns: -1,
          children: [
            {
              field: fieldEnum.inReset,
              colId: fieldEnum.inReset,
              headerName: '',
              menuTabs: ['filterMenuTab'],
              filter: 'agTextColumnFilter',
              suppressHeaderMenuButton: false,
              suppressMovable: true,
              lockPosition: 'left',
              filterParams: agGridUtils.filters.standardStringFilter,
              editable: false,
              maxWidth: 50,
              minWidth: 50,
              resizable: false,
              pinned: 'left',
              context: { required: true },
              hide: !this.hasProductsInResetEnabled,
              cellRenderer: 'ImageRenderer',
              cellRendererParams: params => {
                if (!params.data.inReset) {
                  return {
                    imgComponent: 'NotInResetIcon',
                    classes: this.isEditingDisabled ? 'disabled' : '',
                    title: this.$t('general.productNotSelectedToBeInReset'),
                  };
                }
              },
            },
            {
              field: fieldEnum.isNewProduct,
              colId: fieldEnum.isNewProduct,
              headerName: '',
              editable: false,
              maxWidth: 60,
              minWidth: 60,
              resizable: false,
              suppressMovable: true,
              lockPosition: 'left',
              cellClass: 'justified-center border-left limit-focus-padding chip-cell',
              cellRenderer: params => {
                const productType = this.getProductType(params.data);
                return productType.style !== productModellingTypes.existingProduct.style
                  ? `
                  <span class="rtls-chip ${productType.style}">
                    <span class="rtls-chip__content">
                      ${params.value}
                    </span>
                  </span>
                `
                  : '';
              },
              valueGetter: params => {
                const productType = this.getProductType(params.data);
                return this.getProductTypeDisplayString(productType.translation, this.tkey);
              },
              filterParams: { valueFormatter: params => params.value },
              pinned: 'left',
            },
            {
              field: fieldEnum.productKey,
              colId: fieldEnum.productKey,
              headerName: '',
              editable: false,
              suppressHeaderMenuButton: true,
              suppressMovable: true,
              lockPosition: 'left',
              hide: true,
              context: { required: true },
            },
            {
              field: fieldEnum.productKeyDisplay,
              colId: fieldEnum.productKeyDisplay,
              headerName: this.tkey('attributes.productKeyDisplay'),
              filter: 'agTextColumnFilter',
              menuTabs: ['filterMenuTab'],
              suppressHeaderMenuButton: false,
              suppressMovable: true,
              lockPosition: 'left',
              editable: params => params.data.isNewProduct && !this.isEditingDisabled,
              width: 130,
              pinned: 'left',
              cellClass: 'bold-text',
              context: { required: true },
              cellRenderer: renderProductSummaryCell,
            },
            {
              field: fieldEnum.itemDescription,
              colId: fieldEnum.itemDescription,
              headerName: this.tkey('attributes.itemDescription'),
              menuTabs: ['filterMenuTab'],
              filter: 'agTextColumnFilter',
              suppressHeaderMenuButton: false,
              editable: params => params.data.isNewProduct && !this.isEditingDisabled,
              filterParams: agGridUtils.filters.standardStringFilter,
              minWidth: 300,
              flex: 1,
              pinned: 'left',
              context: { required: true },
            },
            {
              hide: !this.hasKPKUEnabled,
              field: fieldEnum.kpku,
              colId: fieldEnum.kpku,
              headerName: this.tkey('attributes.kpku'),
              suppressHeaderMenuButton: false,
              width: 100,
              context: { required: false },
              pinned: 'left',
            },
            {
              hide: !this.hasClientProductKeyColumnEnabled,
              field: fieldEnum.clientProductKey,
              colId: fieldEnum.clientProductKey,
              headerName: this.tkey('attributes.clientProductKey'),
              suppressHeaderMenuButton: false,
              width: 100,
              editable: this.canEditClientID && this.betaClientIDEditEnabled,
              context: { required: this.hasClientProductKeyColumnEnabled },
              pinned: 'left',
            },
          ],
        },
        productKpis: {
          headerName: this.tkey('attributes.tableHeaders.productKPIs'),
          headerClass: 'product-kpis',
          openByDefault: true,
          groupId: 'product-kpis',
          children: [
            numericColumn({
              colDef: {
                field: fieldEnum.contentValue,
                colId: fieldEnum.contentValue,
                headerName: this.tkey('attributes.contentValue'),
                width: 105,
                context: { required: true },
              },
            }),
            {
              field: fieldEnum.contentUnitOfMeasure,
              colId: fieldEnum.contentUnitOfMeasure,
              headerName: this.tkey('attributes.contentUnitOfMeasure'),
              suppressHeaderMenuButton: false,
              filter: true,
              width: 110,
              context: { required: true },
            },
            numericColumn({
              // Transaction weight stores "no value" as an empty string ('') rather than null,
              // so that empty cells copy/paste consistently and extract generation (which checks
              // strictly for '') keeps working. See AOV3-8741.
              emptyValue: '',
              colDef: {
                field: fieldEnum.averageTransactionWeight,
                colId: fieldEnum.averageTransactionWeight,
                headerName: this.tkey('attributes.averageTransactionWeight'),
                suppressHeaderMenuButton: false,
                width: 110,
                editable: true,
                hide: this.transactionWeightDisabled,
                cellRenderer: 'CellUnitSuffixRenderer',
                cellRendererParams: {
                  valueGetter: ({ data }) => data.averageTransactionWeight,
                  suffixGetter: ({ data }) => data.contentUnitOfMeasure,
                },
                valueFormatter: ({ data }) => {
                  return formatUtils.formatNumberIfNumeric({
                    value: data.averageTransactionWeight,
                    format: 'numberWithThreeDecimals',
                  });
                },
                context: { required: false },
              },
            }),

            {
              field: fieldEnum.packageTypeDescription,
              colId: fieldEnum.packageTypeDescription,
              headerName: this.tkey('attributes.packageTypeDescription'),
              hide: !this.isPackageTypeDescriptionColumnVisible,
              suppressHeaderMenuButton: false,
              editable: this.canEditClientID && this.betaClientIDEditEnabled,
              filter: true,
              width: 110,
            },
            {
              field: fieldEnum.futureId,
              colId: fieldEnum.futureId,
              headerName: this.tkey('attributes.futureId'),
              width: 105,
              context: { required: false },
            },
            numericColumn({
              colDef: {
                field: fieldEnum.unitWidth,
                colId: fieldEnum.unitWidth,
                headerName: `${this.tkey('attributes.unitWidth')}`,
                width: 95,
              },
            }),
            numericColumn({
              colDef: {
                field: fieldEnum.unitLength,
                colId: fieldEnum.unitLength,
                headerName: `${this.tkey('attributes.unitLength')}`,
                width: 95,
              },
            }),
            numericColumn({
              colDef: {
                field: fieldEnum.unitHeight,
                colId: fieldEnum.unitHeight,
                headerName: `${this.tkey('attributes.unitHeight')}`,
                width: 100,
              },
            }),
            {
              field: fieldEnum.palletContent,
              colId: fieldEnum.palletContent,
              hide: !this.hasPalletContentEnabled,
              headerName: this.tkey('attributes.palletContent'),
              valueParser: params => get(params, 'newValue', ''),
              width: 105,
              context: { required: false },
            },
            {
              field: fieldEnum.parentProduct,
              colId: fieldEnum.parentProduct,
              headerName: this.tkey('attributes.parentProduct'),
              editable: params =>
                !get(params, 'data.isNewProduct', false) && !this.isEditingDisabled,
              cellClass: params => get(params, 'data.isNewProduct', false) && 'disabled-cell',
              width: 105,
              context: { required: false },
            },
            {
              field: fieldEnum.dependentSource,
              colId: fieldEnum.dependentSource,
              headerName: this.tkey('attributes.dependentSource'),
              hide: !this.dependentProductsEnabled,
              width: 105,
              context: { required: false },
            },
          ],
        },
        productFinancials: {
          headerName: this.tkey('attributes.tableHeaders.financials'),
          headerClass: 'financials divider-left',
          openByDefault: false,
          groupId: 'financials',
          children: [
            numericColumn({
              displayPrecision: 3,
              colDef: {
                hide: !this.hasTransferPriceRefreshUpdatesPriceAndMarginEnabled,
                field: fieldEnum.originalPurchasePrice,
                colId: fieldEnum.originalPurchasePrice,
                headerName: this.tkey('attributes.originalPurchasePrice'),
                width: 100,
                editable: false,
                context: { required: false },
              },
            }),
            numericColumn({
              displayPrecision: 3,
              colDef: {
                field: fieldEnum.purchasePriceInRange,
                colId: fieldEnum.purchasePriceInRange,
                hide: !this.hasTransferPriceEnabled,
                width: 100,
                editable: false,
                context: { required: false },
              },
            }),
            numericColumn({
              displayPrecision: 3,
              colDef: {
                hide: !this.hasTransferPriceRefreshUpdatesPriceAndMarginEnabled,
                field: fieldEnum.originalTransferPrice,
                colId: fieldEnum.originalTransferPrice,
                headerName: this.tkey('attributes.originalTransferPrice'),
                width: 100,
                editable: false,
                context: { required: false },
              },
            }),
            numericColumn({
              displayPrecision: 3,
              colDef: {
                field: fieldEnum.transferPriceInRange,
                colId: fieldEnum.transferPriceInRange,
                hide: !this.hasTransferPriceEnabled,
                width: 100,
                editable: false,
                context: { required: false },
              },
            }),
            numericColumn({
              multiplier: 100,
              displayPrecision: 2,
              colDef: {
                field: fieldEnum.theoreticalMargin,
                colId: fieldEnum.theoreticalMargin,
                headerName: this.tkey('attributes.margin'),
                width: 100,
                context: { required: true },
                cellClassRules: {
                  ...validationCellClassRules,
                  'diff-background': tracker.hasDiff,
                },
              },
            }),
            numericColumn({
              displayPrecision: 2,
              colDef: {
                field: fieldEnum.theoreticalPrice,
                colId: fieldEnum.theoreticalPrice,
                headerName: `${this.tkey('attributes.price')} (${this.t('lookup.currency')})`,
                width: 90,
                context: { required: true },
                cellClassRules: {
                  ...validationCellClassRules,
                  'diff-background': tracker.hasDiff,
                  'underlined-overridden': params =>
                    agGridUtils.comparators.didValueChange(
                      params.data.theoreticalPrice,
                      params.data.originalPrice
                    ),
                },
              },
            }),
            numericColumn({
              colDef: {
                field: fieldEnum.shrinkToSalesRatio,
                colId: fieldEnum.shrinkToSalesRatio,
                headerName: this.tkey('attributes.shrinkToSalesRatio'),
                headerTooltip: this.tkey('attributes.shrinkToSalesRatioToolTip'),
                width: 120,
                hide: !this.isFreshWP,
                required: this.isFreshWP,
                cellClassRules: {
                  ...validationCellClassRules,
                  'diff-background': tracker.hasDiff,
                  'underlined-overridden': params =>
                    agGridUtils.comparators.didValueChange(
                      params.data.shrinkToSalesRatio,
                      params.data.originalShrinkToSalesRatio
                    ),
                },
                valueFormatter: ({ value }) => this.getRoundedPercentage(value),
                cellEditor: 'CellMarginEditor',
              },
            }),
            numericColumn({
              colDef: {
                columnGroupShow: 'open',
                field: fieldEnum.originalMargin,
                colId: fieldEnum.originalMargin,
                headerName: this.tkey('attributes.originalMargin'),
                headerTooltip: `${this.tkey('attributes.tooltips.originallyCalculatedMargin')}`,
                width: 100,
                editable: false,
                context: { required: false },
                valueFormatter: params => this.formatMargin(params.value),
              },
            }),
            numericColumn({
              colDef: {
                columnGroupShow: 'open',
                field: fieldEnum.originalPrice,
                colId: fieldEnum.originalPrice,
                headerName: `${this.tkey('attributes.originalPrice')} (${this.t('lookup.currency')})`,
                headerTooltip: `${this.tkey('attributes.tooltips.originallyCalculatedPrice')}`,
                width: 90,
                editable: false,
                context: { required: false },
              },
            }),
            numericColumn({
              colDef: {
                columnGroupShow: 'open',
                field: fieldEnum.originalShrinkToSalesRatio,
                colId: fieldEnum.originalShrinkToSalesRatio,
                hide: !this.isFreshWP,
                headerName: this.tkey('attributes.originalShrinkToSalesRatio'),
                width: 150,
                editable: false,
                required: false,
                valueFormatter: params => this.getRoundedPercentage(params.value),
              },
            }),
            {
              hide: !this.hasTransferPriceRefreshUpdatesPriceAndMarginEnabled,
              field: fieldEnum.maintainOption,
              colId: fieldEnum.maintainOption,
              headerName: this.tkey('attributes.maintainOption'),
              cellEditor: 'agSelectCellEditor',
              cellEditorParams: { values: Object.values(maintainOptions) },
              width: 100,
              valueFormatter: params =>
                this.tkey(
                  `attributes.maintainOptions.${params.value ? params.value : 'doNothing'}`
                ),
              valueParser: params => {
                // When copying a 'null' value, it appears to come through as a whitespace character.
                // When pasting from Excel, it pastes as an empty string.
                // This is to force it back to the doNothing value.
                return isEmpty(trim(params.newValue)) ? maintainOptions.doNothing : params.newValue;
              },
              context: { required: false },
            },
            {
              field: fieldEnum.periodID,
              colId: fieldEnum.periodID,
              headerName: this.tkey('attributes.periodID'),
              hide: !this.hasPeriodIDColumnEnabled,
              filter: 'agTextColumnFilter',
              editable: true,
              filterParams: agGridUtils.filters.standardStringFilter,
              suppressHeaderMenuButton: false,
              minWidth: 150,
              flex: 1,
              cellEditor: 'ComboboxEditor',
              cellEditorParams: () => ({ options: [...this.availablePeriodIds] }),
              context: { required: false },
            },
          ],
        },
        productInformation: {
          headerName: this.tkey('attributes.tableHeaders.productInformation'),
          headerClass: 'product-information divider-left',
          openByDefault: false,
          groupId: 'product-information',
          children: [
            {
              field: fieldEnum.productSeries,
              colId: fieldEnum.productSeries,
              headerName: this.tkey('attributes.productSeries'),
              hide: !this.productSeriesEnabled,
              menuTabs: ['filterMenuTab'],
              filter: 'agTextColumnFilter',
              editable: !this.isEditingDisabled,
              filterParams: agGridUtils.filters.standardStringFilter,
              suppressHeaderMenuButton: false,
              minWidth: 150,
              flex: 1,
              cellEditor: 'ComboboxEditor',
              cellEditorParams: () => ({ options: tracker.distinctValues('productSeries') }),
              context: { required: false },
            },
            {
              field: fieldEnum.recentlySold,
              colId: fieldEnum.recentlySold,
              headerName: `${this.tkey('attributes.recentlySold')}`,
              type: 'booleanColumnCustom',
              width: 90,
              boolean: true,
              editable: false,
              headerTooltip: `${this.tkey('attributes.tooltips.recentlySold', { numberWeeks: this.weeksNotSoldForDiscontinued })}`,
            },
            {
              field: fieldEnum.assortment,
              colId: fieldEnum.assortment,
              type: 'booleanColumnCustom',
              headerName: this.tkey('attributes.assortment'),
              filterParams: { valueFormatter: agGridUtils.formatters.booleanStringFormatter },
              cellEditor: 'agSelectCellEditor',
              cellEditorParams: { values: [true, false] },
              width: 90,
              valueParser: agGridUtils.parsers.booleanParser,
              valueFormatter: agGridUtils.formatters.booleanFormatter,
              boolean: true,
              editable: params =>
                !this.isEditingDisabled && !tracker.isCellInSameRowInvalid(params, 'parentProduct'),
            },
            {
              field: fieldEnum.fromTemplate,
              colId: fieldEnum.fromTemplate,
              boolean: true,
              type: 'booleanColumnCustom',
              editable: false,
              headerName: this.tkey('attributes.fromTemplate'),
              hide: !this.templatesEnabled || this.isTemplate,
              width: 95,
              valueFormatter: agGridUtils.formatters.booleanStringFormatter,
              filterParams: { valueFormatter: agGridUtils.formatters.booleanStringFormatter },
            },
            numericColumn({
              colDef: {
                field: this.optionalFieldEnum.minimumFacings.field,
                colId: this.optionalFieldEnum.minimumFacings.field,
                headerName: `${this.tkey('attributes.minimumFacings')}`,
                width: 90,
                hide: !this.showMinimumFacings,
                context: { required: this.showMinimumFacings },
              },
            }),

            numericColumn({
              valuePrecision: 0,
              displayPrecision: 0,
              colDef: {
                field: this.optionalFieldEnum.minimumDistribution.field,
                colId: this.optionalFieldEnum.minimumDistribution.field,
                headerName: `${this.tkey('attributes.minimumDistribution')}`,
                width: 90,
                hide: !this.showMinimumDistribution,
                context: { required: this.showMinimumDistribution },
              },
            }),
            numericColumn({
              valuePrecision: 0,
              displayPrecision: 0,
              colDef: {
                field: this.optionalFieldEnum.seasonalWeekNumbers.field,
                colId: this.optionalFieldEnum.seasonalWeekNumbers.field,
                headerName: this.tkey(
                  `attributes.${this.optionalFieldEnum.seasonalWeekNumbers.field}`
                ),
                width: 90,
                hide: !this.showAdditionalField(this.optionalFieldEnum.seasonalWeekNumbers.field),
                context: {
                  required: this.showAdditionalField(
                    this.optionalFieldEnum.seasonalWeekNumbers.field
                  ),
                },
              },
            }),
            {
              field: this.optionalFieldEnum.userEnteredSisterProduct.field,
              colId: this.optionalFieldEnum.userEnteredSisterProduct.field,
              headerName: this.tkey(
                `attributes.${this.optionalFieldEnum.userEnteredSisterProduct.field}`
              ),
              hide: !this.showAdditionalField(
                this.optionalFieldEnum.userEnteredSisterProduct.field
              ),
              suppressHeaderMenuButton: false,
              filter: true,
              width: 110,
              valueSetter: params => {
                params.data[this.optionalFieldEnum.userEnteredSisterProduct.field] =
                  agGridUtils.validations.valueIsFalsy({ value: params.newValue })
                    ? ''
                    : String(params.newValue);
                return true;
              },
              context: { required: false },
            },
            {
              field: this.optionalFieldEnum.seasonalProduct.field,
              colId: this.optionalFieldEnum.seasonalProduct.field,
              suppressHeaderMenuButton: true, // values are inverted and show up wrong here
              type: 'booleanColumnCustom',
              headerName: this.tkey(`attributes.${this.optionalFieldEnum.seasonalProduct.field}`),
              hide: !this.showAdditionalField(this.optionalFieldEnum.seasonalProduct.field),
              cellEditor: 'agSelectCellEditor',
              cellEditorParams: { values: [true, false] },
              width: 90,
              valueParser: agGridUtils.parsers.booleanParser,
              valueFormatter: agGridUtils.formatters.booleanFormatter,
              boolean: true,
              editable: true,
              context: {
                required: this.showAdditionalField(this.optionalFieldEnum.seasonalProduct.field),
              },
            },
            {
              field: this.optionalFieldEnum.pgMustFlag.field,
              colId: this.optionalFieldEnum.pgMustFlag.field,
              headerName: this.tkey(`attributes.${this.optionalFieldEnum.pgMustFlag.field}`),
              hide: !this.showAdditionalField(this.optionalFieldEnum.pgMustFlag.field),
              filter: 'agTextColumnFilter',
              editable: true,
              filterParams: agGridUtils.filters.standardStringFilter,
              suppressHeaderMenuButton: false,
              minWidth: 150,
              flex: 1,
              cellEditor: 'ComboboxEditor',
              cellEditorParams: () => ({
                options: [...Object.values(pgMustFlagOptions)],
                preventValueToLower: true,
              }),
              context: { required: false },
            },
            numericColumn({
              valuePrecision: 0,
              displayPrecision: 0,
              colDef: {
                field: this.optionalFieldEnum.additionalMinimumDistribution.field,
                colId: `${this.optionalFieldEnum.additionalMinimumDistribution.field}AdditionalAttribute`,
                headerName: this.tkey(
                  `attributes.${this.optionalFieldEnum.additionalMinimumDistribution.field}WoPercentage`
                ),
                width: 90,
                hide: !this.showAdditionalField(
                  this.optionalFieldEnum.additionalMinimumDistribution.field
                ),
                context: {
                  required: this.showAdditionalField(
                    this.optionalFieldEnum.additionalMinimumDistribution.field
                  ),
                },
              },
            }),
            numericColumn({
              valuePrecision: 0,
              displayPrecision: 0,
              colDef: {
                field: this.optionalFieldEnum.maximumDistribution.field,
                colId: this.optionalFieldEnum.maximumDistribution.field,
                headerName: this.tkey(
                  `attributes.${this.optionalFieldEnum.maximumDistribution.field}`
                ),
                width: 90,
                hide: !this.showAdditionalField(this.optionalFieldEnum.maximumDistribution.field),
                context: {
                  required: this.showAdditionalField(
                    this.optionalFieldEnum.maximumDistribution.field
                  ),
                },
              },
            }),

            {
              field: this.optionalFieldEnum.localProduct.field,
              colId: this.optionalFieldEnum.localProduct.field,
              suppressHeaderMenuButton: true, // values are inverted and show up wrong here
              type: 'booleanColumnCustom',
              headerName: this.tkey(`attributes.${this.optionalFieldEnum.localProduct.field}`),
              hide: !this.showAdditionalField(this.optionalFieldEnum.localProduct.field),
              cellEditor: 'agSelectCellEditor',
              cellEditorParams: { values: [true, false] },
              width: 90,
              valueParser: agGridUtils.parsers.booleanParser,
              valueFormatter: agGridUtils.formatters.booleanFormatter,
              boolean: true,
              editable: true,
              context: {
                required: this.showAdditionalField(this.optionalFieldEnum.localProduct.field),
              },
            },
          ],
        },
        deleteColumn: {
          headerName: '',
          groupId: 'deleteGroup',
          children: [
            {
              ...agGridUtils.colDefs.action,
              colId: fieldEnum.deleteNewProduct,
              minWidth: 40,
              maxWidth: 40,
              borders: false,
              pinned: 'right',
              headerClass: 'delete-column',
              cellRenderer: AgGridIcon,
              cellRendererParams: params => {
                if (params.data.isNewProduct) {
                  return {
                    classes: this.isEditingDisabled ? 'greyed-out' : 'selectable',
                    iconComponent: 'TrashIcon',
                    isDisabled: this.isEditingDisabled,
                    onClick: this.openDeleteProductsModal,
                    clickParams: [params.data],
                  };
                }
              },
              cellClassRules: {}, // override default cell classes so it stays white.
            },
          ],
        },
      };
      const columnGroups = keys(columns);
      // Additional column headers can be retrieved from config
      const extraColumns = get(this.getClientConfig, 'attributeEditorColumns', {});
      // Column headers are divided into groups
      // Retrieve and build the config headers for each group if they exist
      each(columnGroups, group => {
        const extraGroupColumns = extraColumns[group];
        if (extraGroupColumns) {
          // Use custom editor for masked date fields
          Object.keys(extraGroupColumns).forEach(colName => {
            if (extraGroupColumns[colName].useDateMask) {
              extraGroupColumns[colName].cellEditor = 'MaskDateEditor';
              extraGroupColumns[colName].cellClassRules = {
                ...validationCellClassRules,
                'invalid-border': this.isDateInvalid,
              };
              this.extraValidations = {
                ...this.extraValidations,
                [colName]: [this.isDateInvalid],
              };
            }
          });

          columns[group].children = agGridUtils.builders.mergeHeaders(
            columns[group].children,
            extraGroupColumns,
            { orderChildren: true }
          );
          each(keys(extraGroupColumns), col => {
            // Fields that should only be automatically mapped during CSV upload if they exist in the config
            this.optionalSchemaFields.add(col);
          });
        }
      });

      return columns;
    },

    isNewRowsNumberValid() {
      return (
        isInteger(this.newProductRowsNumber) &&
        this.newProductRowsNumber > 0 &&
        this.newProductRowsNumber <= maximumRowsToAdd
      );
    },

    isAddNewProductInputDisabled() {
      return this.isSimpleSwapsWP || this.isEditingDisabled;
    },

    isAddButtonDisabled() {
      return !this.isNewRowsNumberValid || this.isAddNewProductInputDisabled;
    },

    isEditingDisabled() {
      return !this.canEditAttributeEditorPage;
    },

    isCleanAttributesDisabled() {
      return this.isEditingDisabled || !size(keys(this.currentCustomAttributes));
    },

    hasNoDataChanges() {
      return !tracker.hasChanges();
    },

    requiredSizeFields() {
      return sizeAttributes[this.selectedWorkpackage.fillInSelection];
    },

    requiredFields() {
      const cols = [
        ...this.initialHeaders.pinnedHeaders.children.filter(c => c.context?.required),
        ...this.initialHeaders.productKpis.children.filter(c => c.context?.required),
        ...this.initialHeaders.productFinancials.children.filter(c => c.context?.required),
        ...this.initialHeaders.productInformation.children.filter(c => c.context?.required),
        ...this.customAttributeColumns,
      ];
      return cols.map(col => col.colId);
    },

    showErrorControls() {
      return tracker.gridHasErrors() || tracker.gridHasWarnings() || !!this.validationFilter;
    },

    invalidRowsErrorMessage() {
      return this.$t('validationErrors.rowsHaveInvalidValues');
    },

    currentCustomAttributes() {
      if (!this.customAttributeColumns) return [];
      console.log('currentCustomAttributes');

      const currentAttributes = filter(
        this.customAttributeColumns,
        ca => !includes(this.deletedAttributes, ca.colId)
      );
      return reduce(
        currentAttributes,
        (acc, attr) => {
          acc[attr.colId] = {
            id: attr.colId,
            name: attr.headerName,
          };
          return acc;
        },
        {}
      );
    },

    customAttributeColumns() {
      return [...this.savedAttributes, ...this.newAttributes];
    },

    customAttributeColumnsByField() {
      return keyBy(this.customAttributeColumns, 'field');
    },

    initialHeadersFields() {
      return [
        ...this.initialHeaders.pinnedHeaders.children.map(c => c.field),
        ...this.initialHeaders.productKpis.children.map(c => c.field),
        ...this.initialHeaders.productFinancials.children.map(c => c.field),
        ...this.initialHeaders.productInformation.children.map(c => c.field),
      ];
    },

    storeClassEligibilityColumnsByField() {
      return keyBy(this.storeClassEligibilityHeaders, 'field');
    },

    storeClassEligibilityHeaderNames() {
      return map(this.storeClassEligibilityHeaders, 'headerName');
    },

    customHeaderFields() {
      return map(this.customAttributeColumns, 'field');
    },

    customHeaderNames() {
      return map(this.customAttributeColumns, 'headerName');
    },

    hasDataChanges() {
      return !(
        this.hasNoDataChanges &&
        isEmpty(this.deletedAttributes) &&
        isEmpty(this.updatedAttributes) &&
        isEmpty(tracker.removedRows) &&
        isEmpty(tracker.addedRows) &&
        isEmpty(this.productsWithoutEligibility)
      );
    },

    hasDataErrors() {
      // This should only return true if we want to prevent save. i.e. to prevent data integrity issues
      return tracker.gridHasBlockingValidations();
    },

    hasNewProducts() {
      return tracker.count({ isNewProduct: true });
    },

    usedHeaders() {
      return flattenDeep(map(this.headers, h => this.getHeaderName(h)));
    },

    renameCustomAttributeRules() {
      return [
        value => !!trim(value) || this.tkey('attributes.renameAttributeDialog.validation.required'),
        value =>
          !this.isCustomAttributeNameInUse(value) ||
          this.tkey('attributes.renameAttributeDialog.validation.unique'),
      ];
    },

    isRenameCustomAttributeValid() {
      return (
        !!trim(this.renamedCustomAttributeName) &&
        !this.isCustomAttributeNameInUse(this.renamedCustomAttributeName)
      );
    },

    storeClasses() {
      if (isEmpty(this.scenarioFurniture)) return [];

      return (this.scenarioFurniture.storeClasses || []).map(({ _id, name }) => ({ _id, name }));
    },

    allSizeFields() {
      return union(...map(sizeAttributes, v => v));
    },

    hasRestOfMarketEnabled() {
      return get(this.getClientConfig, 'features.restOfMarketEnabled', false);
    },

    hasProductsInResetEnabled() {
      return get(this.getClientConfig, 'features.productsInResetEnabled', false);
    },

    hasTransferPriceEnabled() {
      return get(this.getClientConfig, 'features.transferPriceEnabled', false);
    },

    hasTransferPriceRefreshUpdatesPriceAndMarginEnabled() {
      return get(this.getClientConfig, 'features.transferPriceRefreshUpdatesPriceAndMargin', false);
    },

    hasKPKUEnabled() {
      return get(this.getClientConfig, 'features.kpkuEnabled', false);
    },

    hasPalletContentEnabled() {
      return get(this.getClientConfig, 'features.palletContentEnabled', false);
    },

    productSeriesEnabled() {
      return get(this.getClientConfig, 'features.productSeries', false);
    },

    isPackageTypeDescriptionColumnVisible() {
      return get(this.getClientConfig, 'features.isPackageTypeDescriptionColumnVisible', false);
    },

    hasAdditionalFieldsEnabled() {
      return get(
        this.getClientConfig,
        'features.productAttributeEditorPage.additionalFieldsEnabled',
        false
      );
    },

    hasMinimumFacingsEnabled() {
      return get(this.getClientConfig, 'features.minimumFacingsEnabled', false);
    },

    showMinimumFacings() {
      return (
        this.hasMinimumFacingsEnabled &&
        get(this.selectedScenario, 'optionalAttributes.useMinimumFacings')
      );
    },

    hasMinimumDistributionEnabled() {
      return get(this.getClientConfig, 'features.minimumDistributionEnabled', false);
    },

    showMinimumDistribution() {
      return (
        this.hasMinimumDistributionEnabled &&
        get(this.selectedScenario, 'optionalAttributes.useMinimumDistribution')
      );
    },

    isTriangleHidden() {
      return this.isNewRowsNumberValid || this.newProductRowsNumber === '';
    },

    isTemplate() {
      return !!this.selectedWorkpackage.isTemplate;
    },

    totalProductsNotInTemplate() {
      const count = get(tracker.getCounts('fromTemplate'), 'false', 0);
      return count;
    },

    showProductsNotInTemplateWarning() {
      return this.totalProductsNotInTemplate && this.selectedWorkpackage.templateId;
    },

    isClearFiltersDisabled() {
      return isEmpty(this.filterModel) && !this.searchString && !this.validationFilter;
    },

    policyIgnoredImportColumnNames() {
      return uniq(
        compact(
          ignoredFieldIdsForImport.flatMap(fieldId => [fieldId, this.tkey(`attributes.${fieldId}`)])
        )
      );
    },

    mergedFieldsToIgnore() {
      return uniq([...Array.from(this.fieldsToIgnore), ...this.policyIgnoredImportColumnNames]);
    },

    hasPeriodIDColumnEnabled() {
      return get(
        this.getClientConfig,
        'features.productAttributeEditorPage.periodIDColumnEnabled',
        false
      );
    },

    availablePeriodIds() {
      return get(this.selectedWorkpackage, 'periodIds', []);
    },
  },

  watch: {
    // Triggers the automatic filter if a blocking error is found.
    'tracker.state.issueCounts': {
      handler: debounce(function () {
        this.syncValidationFilter();
      }, 15),
      deep: true,
    },

    currentNumericLocale() {
      this.refreshGrid();
    },

    currentLocale() {
      this.refreshGrid();
    },
  },

  async created() {
    await this.init();
    // Signal that page data is ready for the agent to act on.
    useAiStore().setContextReady(true);
  },

  mounted() {
    const aiStore = useAiStore();
    aiStore.enableSidePanel({
      section: 'attribute-editor',
      contextDisplay: [this.selectedScenario?.name].filter(Boolean),
    });
    aiStore.setSidePanelContextFactory(() => this.buildInnerContext());

    // The shell gathers what each app provides after its own bootstrap, so the product-summary
    // component can land after this grid has drawn. Re-render the productKey column once gathering
    // has settled, turning plain text into a link - mechanism 4 of ADR-011 - https://github.com/owg-digital/rtls-one-retail/tree/main/frontend/shell/docs/adrs/011-shared-ux-and-cross-microfrontend-components.md
    // Normally it has settled already; this is the safety net.
    this.stopComponentsSubscription = shellBridge()?.subscribe('componentsLoaded', () => {
      this.gridApi?.refreshCells({ columns: [fieldEnum.productKeyDisplay], force: true });
    });
  },

  beforeUnmount() {
    this.stopComponentsSubscription?.();
  },

  methods: {
    getProductsToExclude() {
      const productsAlreadySelected = tracker.getRowIdsWhere({
        fromRestOfMarket: true,
        isNewProduct: true,
      });
      return [...productsAlreadySelected];
    },

    // Gets the value to copy into target cells when dragging an AG-Grid cell.
    getFillHandleValue(params) {
      const colId = params.column?.getColId?.();
      const colType = params.column?.getColDef?.().type;
      const isNumericColumn =
        colType === 'numericColumnNew' ||
        (Array.isArray(colType) && colType.includes('numericColumnNew'));
      const isMaintainOption = colId === fieldEnum.maintainOption;

      // Only override the behaviour for numeric columns or the Maintain column as we know
      // they have issues. Returning false indicates to use AG-Grid's built-in behaviour.
      if (!isNumericColumn && !isMaintainOption) {
        return false;
      }

      // Sources values represents the values from the cells being dragged.
      // Multiple cells can be dragged down in the same column.
      // Source value will be the raw stored value (i.e. NOT the formatted value show in the grid UI).
      const sourceValues = get(params, 'initialValues', []);
      if (!sourceValues.length) {
        return false;
      }

      // Determine the value to fill the target cell with. If users only ever selected a single source cell,
      // this logic could just be fillValue = sourceValues[0]. The modulo handles where users select
      // multiple source cells e.g. imagine a grid with one column and four rows:
      // | 123 |
      // | 456 |
      // | 789 |
      // | 012 |
      // Now if users select the first two rows of this column (123 and 456) and drag down,
      // we want to repeat those values in order (123, 456, 123, 456) rather than filling all the target cells with
      // the value from the first selected cell (123). Dragging down across multiple columns also works.
      // currentIndex is the 0-based index of the target cell being filled.
      const fillValue = sourceValues[params.currentIndex % sourceValues.length];

      if (isMaintainOption && isNil(fillValue)) {
        return maintainOptions.doNothing;
      }

      // Because this is dervied from sourceValue, this will be raw value (e.g. 1.36, rather than 1,36%).
      // AG-Grid can then apply its usual valueFormatter on top of that for the cell.
      return fillValue;
    },

    ...mapActions(useScenarioProductsStore, [
      'fetchScenarioProducts',
      'saveScenarioProducts',
      'resetProductData',
      'uploadAttributesFromSource',
      'uploadAttributesFromScenario',
      'processCSV',
      'processAttributes',
      'uploadAttributesFromFurniture',
    ]),

    ...mapActions(useScenariosStore, [
      'loadScenario',
      'updateScenario',
      'editCustomAttributes',
      'syncTransferPrice',
      'setScenarioOptionalAttributes',
    ]),
    ...mapActions(useSnackbarStore, ['showWarning', 'showSuccess', 'showError']),
    ...mapActions(useFilesStore, ['uploadCSV']),
    ...mapActions(useFurnitureStore, ['fetchScenarioFurniture', 'setScenarioFurniture']),
    ...mapActions(useToolDataStore, ['fetchProductCategories']),

    closeDeleteNewProductsDialog() {
      this.productsToDelete = [];
      this.isDeleteNewProductsDialogOpen = false;
    },

    getContextMenuItems(params) {
      const baseOptions = ['copy', 'copyWithHeaders'];

      if (this.isEditingDisabled) return baseOptions;

      const clearFilters = {
        name: this.t('actions.clearAllFilters'),
        action: () => this.clearFilters(),
      };

      return [
        ...baseOptions,
        'separator',
        // Column-level
        ...this.getColumnMenuItems(params),
        'separator',
        // Grid-level
        clearFilters,
      ];
    },

    onColumnMoved(params) {
      // On Col move the first cells in the group need to have a divider
      // Here the rows get refreshed when they are moved to the first position
      // and the second col in the group is refreshed also

      // Always refresh headers
      this.gridApi.refreshHeader();

      const col = params.column;
      const groupCols = get(col, 'parent.displayedChildren');
      if (!groupCols || groupCols.length < 2) return;
      // if moved into the first position update it and the second col in the group
      if (col.colId === head(groupCols).colId && groupCols[1]) {
        return this.gridApi.refreshCells({ columns: [col, groupCols[1]] });
      }
      // Otherwise refresh the moved col and the new first col
      this.gridApi.refreshCells({ columns: [col, groupCols[0]] });
    },

    getColumnStateDetails() {
      const scenarioKey = this.selectedScenario._id;
      if (!scenarioKey) return;

      return {
        namePrefix: PAE_COLUMN_STATE_PREFIX,
        stateId: scenarioKey,
        version: PAE_COLUMN_STATE_VERSION,
      };
    },

    saveColumnState() {
      const config = this.getColumnStateDetails();
      const columnDefs = this.gridApi.getColumnDefs();
      gridPreferences.saveColumnState(config, columnDefs);
    },

    // A method to restore the initial column state
    loadColumnState() {
      // Load saved columns preferences from local storage
      const config = this.getColumnStateDetails();
      const savedState = gridPreferences.loadColumnState(config);

      if (savedState) {
        const currentColDefs = this.gridApi.getColumnDefs();
        const mergedState = gridPreferences.mergeColumnStates(savedState, currentColDefs);
        this.gridApi.setGridOption('columnDefs', mergedState);
      }
    },

    // A method to merge current column preferences with the saved order from local storage.
    // Needed to resolve conflicts in case different users added/deleted columns on the page.
    mergeColumnStates(savedState) {
      const currentColDefs = this.gridApi.getColumnDefs();
      const mergedState = gridPreferences.mergeColumnStates(savedState, currentColDefs);
      return mergedState;
    },

    restoreOriginalColumnOrder() {
      // TODO
    },

    refreshGrid() {
      this.refreshHeaders();
      this.gridApi.refreshCells({ force: true });
    },

    openDeleteProductsModal(productsToDelete) {
      this.productsToDelete = productsToDelete;
      this.isDeleteNewProductsDialogOpen = true;
    },

    async onCustomAttrAdd(attributeName) {
      this.headers.forEach(h => this.getHeaderName(h));
      if (this.usedAttributeHeaders.includes(attributeName) || isEmpty(attributeName)) {
        return;
      }

      const productKeys = map(this.$options.productsData, 'productKey');
      // Get the index of current columns (after possible reordering) to define where to insert the new attribute
      const index = this.headers.findIndex(h => h.groupId === 'custom-attributes');
      const customAttrsGroup = this.headers[index];
      const newAttribute = this.createAttributeHeader(attributeName, attributeName);
      customAttrsGroup.children.push(newAttribute);
      this.headers.splice(index, 1, customAttrsGroup);

      const mappings = [
        {
          key: attributeName,
          selectedField: attributeName,
          mongoField: null,
          isNew: true,
        },
      ];

      const attributeNames = [attributeName];
      const uploadPromise = this.processAttributes({
        productKeys,
        source: uploadMethods.CUSTOM,
        attributeSource: '',
        mappings,
        attributeNames,
      });
      const [err, uploadData] = await to(uploadPromise);

      if (err) {
        this.isGridLoading = false;
        return;
      }

      const {
        tableRows: scenarioProductsUpdates,
        additionalInformation: { customNewKeys },
      } = uploadData;
      // Check for existing / duplicate attributes and update headers
      this.updateAttributesAfterImport(customNewKeys);
      this.updateRows(scenarioProductsUpdates);

      this.isGridLoading = false;
      const customAttributes = get(this.selectedScenario, 'customAttributes', []);
      if (this.customHeaderFields.length && !customAttributes.length) {
        // resize the columns if there are new custom attributes added via csv and there were no custom attributes on the scenario previously
        // this is needed as otherwise the new attributes will render off screen as the custom attrs header wasn't initially present
        this.resizeColumnGroup('product-kpis');
      }
      return customNewKeys;
    },

    getHeaderName(obj) {
      const headers = [];
      const headerName = get(obj, 'headerName');
      if (headerName) {
        headers.push(headerName);
      }
      const children = get(obj, 'children');
      if (children) {
        headers.push(children.map(ch => this.getHeaderName(ch)));
      }
      return headers;
    },

    /**
     * Resolves a field ID (including custom attribute GUIDs) to a human-readable display name.
     * Used by AI agent actions to avoid surfacing raw GUIDs in error feedback.
     * @param {string} fieldName
     * @returns {string}
     */
    resolveFieldDisplayName(fieldName) {
      // Custom attribute GUID → headerName
      const customAttr = this.customAttributeColumnsByField?.[fieldName];
      if (customAttr?.headerName) return customAttr.headerName;
      // Standard field → translated column header via existing mapper
      try {
        const col = this.gridApi?.getColumn(fieldName);
        if (col) return this.mapHeaderNames({ column: col });
      } catch {
        // fall through to raw field name
      }
      return fieldName;
    },

    getColumnMenuItems(params) {
      if (this.isEditingDisabled) return [];

      const renameAttributeMenuItem = {
        name: this.tkey('attributes.actions.renameAttr'),
        action: () => this.openRenameCustomAttributeDialog(params.column.colId),
      };

      const deleteAttributeMenuItem = {
        name: this.tkey('attributes.actions.deleteAttr'),
        action: () => this.deleteAttribute(params.column.colId),
      };

      const groupId = get(params, 'column.parent.groupId');

      return groupId === 'custom-attributes'
        ? [renameAttributeMenuItem, deleteAttributeMenuItem, this.attributeGroupingMenuEl(params)]
        : [this.attributeGroupingMenuEl(params)];
    },

    openRenameCustomAttributeDialog(attributeId) {
      const attribute = this.customAttributeColumnsByField[attributeId];
      if (!attribute) return;

      this.customAttributeIdToRename = attributeId;
      this.renamedCustomAttributeName = attribute.headerName;
      this.isRenameCustomAttributeDialogOpen = true;
    },

    closeRenameCustomAttributeDialog() {
      this.isRenameCustomAttributeDialogOpen = false;
      this.customAttributeIdToRename = null;
      this.renamedCustomAttributeName = '';
    },

    isCustomAttributeNameInUse(attributeName) {
      const trimmedName = trim(attributeName);
      if (!trimmedName) return false;

      const currentAttribute = this.customAttributeColumnsByField[this.customAttributeIdToRename];
      const otherHeaders = [...this.usedHeaders];
      const currentHeaderIndex = otherHeaders.indexOf(currentAttribute?.headerName);
      if (currentHeaderIndex !== -1) otherHeaders.splice(currentHeaderIndex, 1);

      return otherHeaders.some(header => toLower(header) === toLower(trimmedName));
    },

    renameCustomAttribute() {
      if (!this.isRenameCustomAttributeValid) return;

      const attributeId = this.customAttributeIdToRename;
      const name = trim(this.renamedCustomAttributeName);
      const newAttribute = this.newAttributes.find(attribute => attribute.colId === attributeId);

      if (newAttribute) {
        newAttribute.headerName = name;
      } else {
        this.updatedAttributes = {
          ...this.updatedAttributes,
          [attributeId]: { id: attributeId, name },
        };
      }

      this.closeRenameCustomAttributeDialog();
      this.refreshHeaders();
    },

    attributeGroupingMenuEl(params) {
      return {
        name: this.tkey('attributes.actions.createAttrGrouping'),
        action: () => {
          const colDef = params.column.colDef;
          const groupVals = new Set();
          this.groupingColumn = colDef;
          this.productsData.forEach(node => {
            if (node[colDef.colId]) {
              groupVals.add(node[colDef.colId]);
            }
          });
          this.groupingValues = Array.from(groupVals);
          this.groupingModalOpen = true;
        },
      };
    },

    createAttributeHeader(name, id) {
      return {
        headerName: name,
        field: id,
        colId: id,
        minWidth: 100,
        flex: 1,
        cellEditor: 'ComboboxEditor',
        cellEditorParams: () => ({
          options: tracker.distinctValues(id),
          /* Some attributes are automatically copied as is from tooldata-attributes when we create a scenario,
          others, added manually by the user via `Add attrs` option. The first are copied as is from data feed,
          the later are cleaned up (lowercased/trimmed) before being stored in mongo. So we prevent lower option as
          1) we don't want to change the original value that may not be lowered cased and
          2) we want to keep consistency of stored values and incoming changes, so we don't have `foo` and `Foo` stored.
          */
          preventValueToLower: true,
        }),
        menuTabs: ['filterMenuTab', 'generalMenuTab'], // menu tab shows the deletion option
        resizable: true,
      };
    },

    createStoreClassEligibilityHeaders() {
      this.storeClassEligibilityHeaders = [];
      this.storeClassEligibilityFields = [];

      this.storeClasses.forEach(sc => {
        this.storeClassEligibilityHeaders.push({
          headerName: sc.name,
          minWidth: 120,
          flex: 1,
          editable: !this.isEditingDisabled,
          resizable: true,
          field: `restructuredEligibility.${sc._id}.value`,
          colId: `restructuredEligibility.${sc._id}.value`,
          valueParser: agGridUtils.parsers.booleanParser,
          cellRenderer: params =>
            agGridUtils.utils.checkboxRenderer(params, this.handleCheckboxChange),
          cellRendererParams: { field: `restructuredEligibility.${sc._id}.value` },
          boolean: true,
        });
        this.storeClassEligibilityFields.push(`restructuredEligibility.${sc._id}.value`);
      });

      return this.storeClassEligibilityHeaders;
    },

    closeGroupingModal() {
      this.groupingModalOpen = false;
      this.groupingColumn = {};
      this.groupingValues = [];
    },

    closeDependencyTreeModal() {
      this.dependencyTreeModalOpen = false;
      this.dependencyTreeFeedback = {};
    },

    openAddOptionalColumnsModal() {
      this.isAddOptionalColumnsModalOpen = true;
    },

    closeAddOptionalColumnsModal() {
      this.isAddOptionalColumnsModalOpen = false;
    },

    openRestOfMarketProductsModal() {
      this.isRestOfMarketProductsModalOpen = true;
    },

    closeRestOfMarketProductsModal() {
      this.isRestOfMarketProductsModalOpen = false;
    },

    async openResetFromTemplateModal() {
      this.isResetFromTemplate = true;
      this.uploadedDataDetails = await this.onScenarioDataUpload();
    },

    openResetDataModal() {
      this.isResetDataModalOpen = true;
    },

    closeResetDataModal() {
      this.isResetFromTemplate = false;
      this.isResetDataModalOpen = false;
    },

    openCleanAttributesModal() {
      this.isCleanAttributesModalOpen = true;
    },

    closeCleanAttributesModal() {
      this.isCleanAttributesModalOpen = false;
    },

    setSavedAttributes() {
      const customAttributes = get(this.selectedScenario, 'customAttributes', []);
      const savedAttributes = customAttributes.map(attr =>
        this.createAttributeHeader(attr.name, attr.id)
      );

      this.savedAttributes = savedAttributes;
    },

    getInitialCustomAttributesValuesAsObj() {
      const scenarioCustomAttributes = get(this.selectedScenario, 'customAttributes', []);
      const attributes = [...scenarioCustomAttributes, ...this.newAttributes];

      const customAttributesObj = {};

      attributes.forEach(attribute => {
        customAttributesObj[attribute.id || attribute.colId] = '';
      });

      return customAttributesObj;
    },

    getInitialCustomAttributesValues() {
      // Get initial values for both existing scenario attributes and new attributes
      const scenarioCustomAttributes = get(this.selectedScenario, 'customAttributes', []);
      const attributes = [...scenarioCustomAttributes, ...this.newAttributes];

      return map(attributes, attribute => ({
        id: attribute.id || attribute.colId,
        value: '',
      }));
    },

    getInitialStoreClassEligibility() {
      const initialStoreClassEligibility = [];
      const restructuredEligibility = {};
      this.storeClasses.forEach(sc => {
        initialStoreClassEligibility.push({ id: sc._id, value: true });
        restructuredEligibility[sc._id] = { id: sc._id, value: true };
      });
      return { initialStoreClassEligibility, restructuredEligibility };
    },

    // Inspects the product for store class eligibility and creates restructured eligibility object
    // with appropriate values for each store class
    createRestructuredEligibility(storeClassEligibility) {
      return keyBy(storeClassEligibility, 'id');
    },

    setRestructuredEligibilityFromData(scenarioProduct, targetObj, defaultValues) {
      if (scenarioProduct.storeClassEligibility) {
        targetObj.restructuredEligibility = this.createRestructuredEligibility(
          scenarioProduct.storeClassEligibility
        );
        scenarioProduct.restructuredEligibility = groupBy(
          scenarioProduct.storeClassEligibility,
          'id'
        );
      } else {
        this.productsWithoutEligibility.push(`${scenarioProduct.productKey}`);
        const combinedEligibility = merge({}, defaultValues);
        set(targetObj, 'restructuredEligibility', combinedEligibility);
      }
    },

    setKeyIdMap() {
      this.keyIdMap = this.$options.productsData.reduce((a, c) => set(a, c.productKey, c._id), {});
    },

    async deleteRows() {
      tracker.applyTransaction({ remove: this.productsToDelete });
      this.closeDeleteNewProductsDialog();
    },

    handleGridEdit(params) {
      tracker.handleEdit(params);
    },

    async deleteAttribute(attributeId) {
      const savedAttributeIds = map(this.savedAttributes, 'colId');
      if (savedAttributeIds.includes(attributeId)) this.deletedAttributes.push(attributeId);
      else this.newAttributes = this.newAttributes.filter(attr => attr.colId !== attributeId);

      tracker.removeColumn(attributeId);

      // Attributes are now accurate - reassign the headers to reflect this.
      this.refreshHeaders();

      this.attributesToClean.delete(attributeId);
    },

    // recursively search if attribute exists in cann groups
    findInCannGroups(cannGroups, attributeId) {
      return find(cannGroups, cannGroup => {
        if (cannGroup.attributeId === attributeId) {
          return cannGroup;
        }
        return cannGroup.children && this.findInCannGroups(cannGroup.children, attributeId);
      });
    },

    getMinProductKey() {
      const productKeys = [];
      this.gridApi.forEachNode(({ data }) => {
        if (data.productKey < 0) productKeys.push(data.productKey);
      });

      return min(productKeys) ?? 0;
    },

    getVisibleNewProductsToDelete() {
      const visibleNewProducts = [];
      this.gridApi.forEachNodeAfterFilter(rowNode => {
        if (rowNode.data.isNewProduct) visibleNewProducts.push(rowNode.data);
      });

      return visibleNewProducts;
    },

    getDefaultNewProduct() {
      const eligibility = this.getInitialStoreClassEligibility();

      const margin = '';
      const { newInTemplate, newInScenario } = productOriginSourceTypes;

      return {
        scenarioId: this.selectedScenario._id,
        itemDescription: '',
        assortment: true,
        recentlySold: false,
        isNewProduct: true,
        customAttributes: this.getInitialCustomAttributesValues(),
        storeClassEligibility: eligibility.initialStoreClassEligibility,
        restructuredEligibility: eligibility.restructuredEligibility,
        rateOfSale: 0,
        unitWidth: 0,
        unitLength: 0,
        unitHeight: 0,
        rateOfSaleOverride: null,
        totalSalesOverride: null,
        salesParticipationIndex: 0,
        storeCount: 0,
        trendApplied: null,
        sisterProducts: [],
        packageTypeDescription: '',
        clientProductKey: '',
        originalPrice: 0,
        price: 0,
        originalMargin: margin,
        margin,
        theoreticalMargin: margin,
        fromTemplate: this.isTemplate,
        originSource: this.isTemplate ? newInTemplate : newInScenario,
        inReset: true,
        minimumFacings: this.showMinimumFacings ? 1 : null,
        minimumDistribution:
          this.showMinimumDistribution || this.showAdditionalField('minimumDistribution')
            ? 0
            : null,
        // additional fields
        seasonalWeekNumbers: this.showAdditionalField('seasonalWeekNumbers') ? 0 : null,
        userEnteredSisterProduct: this.showAdditionalField('userEnteredSisterProduct') ? '' : null,
        seasonalProduct: this.showAdditionalField('seasonalProduct') ? false : null,
        pgMustFlag: this.showAdditionalField('pgMustFlag') ? '' : null,
        maximumDistribution: this.showAdditionalField('maximumDistribution') ? 0 : null,
        localProduct: this.showAdditionalField('localProduct') ? false : null,
        palletContent: '',
        averageTransactionWeight: '',
        furnitureArchetypeProductExceptionalEffectiveDate: '',
        furnitureArchetypeProductExceptionalExpiryDate: '',
        maintainOption: this.hasTransferPriceRefreshUpdatesPriceAndMarginEnabled
          ? maintainOptions.marginRate
          : maintainOptions.doNothing,
        ...this.getInitialCustomAttributesValuesAsObj(),
        hasBeenModelled: false,
        chainIntroductionDate: null,
        currentlyObservedSpacebreak: null,
        dateOfObservedSpacebreakChange: null,
        previouslyObservedSpacebreak: null,
      };
    },

    addROMProducts(newProducts) {
      // If a Brand custom attribute exists, populate the value
      const brandColumn = find(
        this.currentCustomAttributes,
        attr => attr.name === this.tkey('attributes.brand')
      );

      const newData = map(newProducts, p => {
        const productKey = p.productKey;
        const productKeyDisplay = p.productKey.toString();

        // Add productKey value to ROM lookup. Once products have been added,
        // we want to exclude the products from the ROM modal so that they cannot
        // be reselected.
        // this.lookupROMProductKeys.add(productKey);
        // Add this new product to list of products without eligibility
        this.productsWithoutEligibility.push(productKey);
        return {
          productKey,
          productKeyDisplay,
          ...this.getDefaultNewProduct(),
          itemDescription: p.itemDescription,
          ...(brandColumn && { [brandColumn.id]: p.brandName }),
          fromRestOfMarket: true,
        };
      });

      this.addRows(newData);
    },

    addNewProducts() {
      const minProductKey = this.getMinProductKey();
      // rows are added top to bottom, so we first add the minimum (negative) product key index
      // e.g. range(-3, 0) -> [p-3, p-2, p-1]
      const newData = range(minProductKey - Math.abs(this.newProductRowsNumber), minProductKey).map(
        newProductKey => {
          // Add this new product to list of products without eligibility
          this.productsWithoutEligibility.push(newProductKey);

          return {
            productKey: newProductKey,
            productKeyDisplay: `p${newProductKey}`, // 'p-3', 'p-4' etc; user will be able to edit that value
            ...this.getDefaultNewProduct(),
          };
        }
      );
      this.addRows(newData);
      this.newProductRowsNumber = '';
    },

    updateFieldImportData(field, isEnabled) {
      const columnName = this.tkey(`attributes.${field}`);
      if (isEnabled && !this.isFieldIgnoredForImport(field)) {
        this.optionalSchemaFields.add(field);
        this.fieldsToIgnore.delete(columnName);
      } else {
        this.optionalSchemaFields.delete(field);
        this.fieldsToIgnore.add(columnName);
      }
    },

    /** specific optional columns */
    async addOptionalColumns(dataToSet) {
      let useMinimumFacings = this.selectedScenario.optionalAttributes.useMinimumFacings;
      let useMinimumDistribution = this.selectedScenario.optionalAttributes.useMinimumDistribution;
      const additionalFields = this.selectedScenario.optionalAttributes.additionalFields;
      this.backupAdditionalFields = {
        ...this.selectedScenario.optionalAttributes.additionalFields,
      };
      each(dataToSet, d => {
        if (d.field === this.optionalFieldEnum.minimumFacings.field) {
          useMinimumFacings = d.setTo;
          this.updateFieldImportData(d.field, useMinimumFacings);
          return;
        }

        if (
          d.field === this.optionalFieldEnum.minimumDistribution.field &&
          !this.hasAdditionalFieldsEnabled
        ) {
          useMinimumDistribution = d.setTo;
          this.updateFieldImportData(d.field, useMinimumDistribution);
          return;
        }

        this.updateFieldImportData(d.field, d.setTo);
        additionalFields[d.field] = d.setTo;
      });

      if (
        !this.hasAdditionalFieldsEnabled &&
        this.selectedScenario.optionalAttributes.useMinimumFacings === useMinimumFacings &&
        this.selectedScenario.optionalAttributes.useMinimumDistribution === useMinimumDistribution
      ) {
        // nothing to do if the settings didn't change
        return;
      }

      // make the changes
      this.isGridLoading = true;
      const productUpdates = this.productsData.map(product => {
        const changedProduct = { productKey: product.productKey };
        // if you are setting the minimumFacings, default it to 1 instead of displaying null
        changedProduct.minimumFacings = useMinimumFacings ? product.minimumFacings || 1 : null;
        // if you are setting the minimumDistribution, default is 0 instead of displaying null
        changedProduct.minimumDistribution =
          useMinimumDistribution || additionalFields.minimumDistribution
            ? product.minimumDistribution || 0
            : null;
        changedProduct.seasonalWeekNumbers = additionalFields.seasonalWeekNumbers
          ? product.seasonalWeekNumbers || 0
          : null;
        changedProduct.userEnteredSisterProduct = additionalFields.userEnteredSisterProduct
          ? product.userEnteredSisterProduct || ''
          : null;
        changedProduct.seasonalProduct = additionalFields.seasonalProduct
          ? !!product.seasonalProduct
          : null;
        changedProduct.pgMustFlag = additionalFields.pgMustFlag ? product.pgMustFlag || '' : null;
        changedProduct.maximumDistribution = additionalFields.maximumDistribution
          ? product.maximumDistribution || 0
          : null;
        changedProduct.localProduct = additionalFields.localProduct ? !!product.localProduct : null;

        return changedProduct;
      });
      // save the settings - to use or not use options back to the scenario
      await this.setScenarioOptionalAttributes({
        useMinimumFacings,
        useMinimumDistribution,
        additionalFields,
      });
      this.updateRows(productUpdates);
      this.gridApi.setColumnsVisible(['minimumFacings'], !!this.showMinimumFacings);
      this.gridApi.setColumnsVisible(['minimumDistribution'], !!this.showMinimumDistribution);
      each(keys(this.selectedScenario.optionalAttributes.additionalFields), field => {
        // Since we have two types of Minimum Distribution this way we can use two differents columns in AgGrid
        const columnId =
          field === 'minimumDistribution' ? 'minimumDistributionAdditionalAttribute' : field;
        this.gridApi.setColumnsVisible([columnId], !!this.showAdditionalField(field));
      });
      // Persist the correct visibility to localStorage so that subsequent loadColumnState() calls
      // restore columns in the right shown/hidden state.
      this.saveColumnState();
      this.isGridLoading = false;
    },

    async resetProducts(dataToReset) {
      this.isGridLoading = true;
      const resetData = await this.resetProductData(dataToReset);
      const scenarioProducts = values(resetData.data.scenarioProducts).map(product => {
        // storeClassEligibility is a nested array in MongoDB but the grid columns read
        // from the flat restructuredEligibility.{id}.value format — convert it here.
        if (product.storeClassEligibility) {
          product.restructuredEligibility = this.createRestructuredEligibility(
            product.storeClassEligibility
          );
        }
        return product;
      });

      this.updateRows(scenarioProducts);
      this.isGridLoading = false;
      this.showSuccess(this.tkey('attributes.successResetProductKPIsMessage'));
    },

    async onCSVUpload(formData) {
      const scenarioId = this.selectedScenario._id;
      formData.append('scenarioId', scenarioId);
      formData.append('optionalSchemaFields', Array.from(this.optionalSchemaFields));
      return this.uploadCSV({ formData, service: 'scenario-products' });
    },

    onSourceDataUpload(attributeNames) {
      return this.uploadAttributesFromSource({
        attributeNames,
        optionalSchemaFields: Array.from(this.optionalSchemaFields),
      });
    },

    async onScenarioDataUpload(scenarioId) {
      // When resetting only the template Id needs to be passed
      const uploadAttributeParams = this.isResetFromTemplate
        ? { templateId: this.selectedWorkpackage.templateId }
        : { sourceScenarioId: scenarioId };
      uploadAttributeParams.optionalSchemaFields = Array.from(this.optionalSchemaFields);

      const omitOptionalSchemaFields = [];

      // If the periodID column is enabled we should exclude from being imported
      // from other scenarios as described in the ACs of AOV3-6535
      if (this.hasPeriodIDColumnEnabled && !this.isResetFromTemplate) {
        omitOptionalSchemaFields.push('periodID');
      }

      uploadAttributeParams.optionalSchemaFields =
        uploadAttributeParams.optionalSchemaFields.filter(
          field => !omitOptionalSchemaFields.includes(field)
        );

      const result = await this.uploadAttributesFromScenario(uploadAttributeParams);
      // Resulting mappings need an additional translation for key and default selectedKey,
      // as those might have lookups in values.
      result.mappings = map(result.mappings, mapping => {
        const tKey = mapping.translationKey;
        if (tKey) {
          mapping.key = this.$t(tKey);
          mapping.selectedField = this.$t(tKey);
        }
        return mapping;
      });
      return result;
    },

    async onFurnitureUpload() {
      const result = await this.uploadAttributesFromFurniture();
      // Resulting mappings need an additional translation for key and default selectedKey,
      // as those might have lookups in values.
      result.mappings = map(result.mappings, mapping => {
        const tKey = mapping.translationKey;
        if (tKey) {
          mapping.key = this.$t(tKey);
          mapping.selectedField = this.$t(tKey);
        }
        return mapping;
      });
      return result;
    },

    async processData(mappings = []) {
      if (!this.isResetFromTemplate) {
        mappings = [...this.uploadedDataDetails.mappings, ...mappings];
      }
      // Send actual attribute names as well to perform lookup on server side
      const actualAttributeNames = map(this.dataToUploadFromSource.attributes, 'attributeName');
      await this.process({
        fileId: this.uploadedDataDetails._id,
        delimiter: this.delimiter,
        mappings,
        attributeSource: this.dataToUploadFromSource.source,
        attributeNames: actualAttributeNames,
        sourceScenarioId: null,
        templateId: this.selectedWorkpackage.templateId,
      });
      this.closeResetDataModal();
    },

    async process({
      fileId,
      mappings,
      delimiter,
      attributeSource,
      attributeNames,
      sourceScenarioId,
      templateId,
      uploadMethod,
    }) {
      this.isGridLoading = true;

      const productKeys = map(this.$options.productsData, 'productKey');
      const uploadPromise = fileId
        ? this.processCSV({ fileId, mappings, delimiter }) // Upload CSV and get feedback from server
        : this.processAttributes({
            // Upload attributes data and get feedback from server
            productKeys,
            source: uploadMethod,
            attributeSource,
            mappings,
            attributeNames,
            sourceScenarioId,
            templateId,
          });

      const [err, uploadData] = await to(uploadPromise);

      if (err) {
        this.isGridLoading = false;
        return;
      }

      const {
        tableRows: scenarioProductsUpdates,
        additionalInformation: { customNewKeys, customKeyUpdates },
      } = uploadData;

      // ignore parent product values for new products on csv import
      const productUpdates = scenarioProductsUpdates.map(product => {
        if (product.productKey < 0) product.parentProduct = undefined;

        return product;
      });
      // Check for existing/ duplicates attributes and update headers
      this.updateAttributesAfterImport(customNewKeys, customKeyUpdates);
      this.updateRows(productUpdates);

      this.isGridLoading = false;
      const customAttributes = get(this.selectedScenario, 'customAttributes', []);
      if (this.customHeaderFields.length && !customAttributes.length) {
        // resize the columns if there are new custom attributes added via csv and there were no custom attributes on the scenario previously
        // this is needed as otherwise the new attributes will render off screen as the custom attrs header wasn't initially present
        this.resizeColumnGroup('product-kpis');
      }
    },

    updateAttributesAfterImport(uploadedAttributes, customKeyUpdates) {
      const attributesFromUpload = uploadedAttributes.map(attr => {
        const header = this.createAttributeHeader(attr.name, attr.id);
        // enrich new attributes with source data
        return {
          ...header,
          source: attr.source,
          attributeSource: attr.attributeSource,
          attributeKey: attr.attributeKey,
        };
      });

      attributesFromUpload.forEach(attr => {
        tracker.addColumn(attr.colId);
      });

      // Currently this always appends - if we want to prevent duplicate uploads before save
      // Then we can check the difference here by the headerName field
      // However, you also need to handle the products as they come back with a unique attributeId
      // and you would need to map this to ensure new headers values are repeatedly applied to the same
      // original column

      this.updatedAttributes = { ...this.updatedAttributes, ...keyBy(customKeyUpdates, 'id') };
      this.newAttributes = [...this.newAttributes, ...attributesFromUpload];

      this.refreshHeaders();

      // Config setting determines whether the new attributes should be cleaned by default
      const defaultCleanSetting = get(this.getClientConfig, 'features.cleanAttributesEnabled');
      if (defaultCleanSetting) {
        each(this.newAttributes, na => this.attributesToClean.add(na.colId));
      }
    },

    resizeColumnGroup(groupId) {
      const colGroup = this.gridApi.getColumnGroup(groupId);
      const groupedCols = colGroup.children.map(x => x.colId);
      this.gridApi.autoSizeColumns(groupedCols);
    },

    mergeUnsavedUpdateHeaders(originalAttr) {
      if (this.updatedAttributes[originalAttr.colId]) {
        originalAttr.headerName = this.updatedAttributes[originalAttr.colId].name;
      }
      return originalAttr;
    },

    setHeaders() {
      // This method constructs the headers and sets them for the grid.

      // Always set saved attributes from the scenario, even on remount
      this.setSavedAttributes();

      // We can delete saved attributes - if they're marked for deletion, don't display them.
      const undeletedAttributes = this.savedAttributes.filter(
        x => !this.deletedAttributes.includes(x.colId)
      );

      // Use updated values returned from reset template
      const allAttributes = [...undeletedAttributes, ...this.newAttributes].map(
        this.mergeUnsavedUpdateHeaders
      );

      // Attribute headers are all saved attributes (unless marked for deletion) and any newly imported ones.
      const attributeHeaders = {
        headerName: this.tkey('attributes.tableHeaders.customAttributes'),
        headerClass: 'custom-attributes divider-left custom-attribute-header',
        groupId: 'custom-attributes',
        // This will be used for tab styling in the future - disabled temporarily
        // headerGroupComponent: 'customAttributesHeaderGroupComponent',
        children: allAttributes,
      };

      // Each of these is its own column group.
      const headers = [
        this.initialHeaders.pinnedHeaders,
        this.initialHeaders.productKpis,
        this.initialHeaders.productFinancials,
        this.initialHeaders.productInformation,
        attributeHeaders,
        this.initialHeaders.deleteColumn,
      ];

      if (!this.isSimpleSwapsWP && this.storeClasses.length > 0) {
        // storeClass eligibility headers
        const storeClassEligibilityHeaders = {
          headerName: this.tkey('attributes.tableHeaders.storeClassEligibility'),
          headerClass: 'storeClass-eligibility divider-left',
          groupId: 'storeClass-eligibility',
          // This will be used for tab styling in the future - disabled temporarily
          // headerGroupComponent: 'customAttributesHeaderGroupComponent',
          children: this.createStoreClassEligibilityHeaders(),
        };

        headers.splice(3, 0, storeClassEligibilityHeaders);
      }

      this.gridApi.setGridOption('columnDefs', headers);
      this.loadColumnState();
      return headers;
    },

    addRows(newData) {
      this.productsData.push(...newData);
      this.addedProducts.push(...newData);
      tracker.applyTransaction({ add: newData, addIndex: 0 });
    },

    updateRows(partialUpdate) {
      this.isGridLoading = true;
      if (isEmpty(partialUpdate)) {
        return;
      }

      this.isSyncingProductData = false;

      // The tracker owns full-row materialisation, reaction application, and
      // the AG Grid transaction. Callers only need to express intent (partial changes).
      tracker.applyTransaction({ update: partialUpdate });
      this.isGridLoading = false;
    },

    resetChanges() {
      this.productsWithoutEligibility = [];
      this.savedAttributes = [];
      this.newAttributes = [];
      this.updatedAttributes = {};
      this.deletedAttributes = [];
      this.deletedProducts = [];
      this.addedProducts = [];
      this.attributesToClean.clear();
      this.currentScenarioStateDiff = {};
      this.productsWithoutPurchasePriceInformation = [];
      this.userEnteredProductMarginsWithoutFunding = [];
      this.backupAdditionalFields = {};
      this.resetAttributesToClean();
    },

    mapHeaderNames(params) {
      if (params.column.colId === fieldEnum.isNewProduct) {
        return this.tkey('attributes.productType');
      }

      const field = params.column.colDef.field;
      const foundAttribute =
        get(this.customAttributeColumnsByField, field) ||
        get(this.storeClassEligibilityColumnsByField, field);

      let translationKey = field;

      // If the feature flag is enabled we need to display the translation without the percentage
      if (this.hasAdditionalFieldsEnabled && translationKey === 'minimumDistribution') {
        translationKey = `${translationKey}WoPercentage`;
      }

      if (translationKey === 'theoreticalMargin') {
        translationKey = 'margin';
      }

      if (translationKey === 'theoreticalPrice') {
        translationKey = 'price';
      }

      // If this is a custom attribute, return header name, otherwise return translation key
      return foundAttribute ? foundAttribute.headerName : this.tkey(`attributes.${translationKey}`);
    },

    processCellCallback(params) {
      // Being called for each cell

      // Map product type values
      if (params.column.colId === fieldEnum.isNewProduct) {
        const productType = this.getProductType(params.node.data);
        return this.tkey(`attributes.${productType.translation}`);
      }

      if (params.column.colId === fieldEnum.theoreticalMargin) {
        const margin = params.node.data[fieldEnum.theoreticalMargin];
        const marginNum = formatUtils.stringToNumberIfNumeric(margin);
        if (isString(marginNum)) return marginNum;
        return formatUtils.formatNumberIfNumeric({ value: marginNum * 100, format: 'export' });
      }

      if (
        params.column.colId === fieldEnum.originalMargin &&
        params.node.data[fieldEnum.originalMargin]
      ) {
        const marginNum = formatUtils.stringToNumberIfNumeric(
          params.node.data[fieldEnum.originalMargin]
        );
        if (isString(marginNum)) return marginNum;
        return formatUtils.formatNumberIfNumeric({ value: marginNum * 100, format: 'export' });
      }
      return agGridUtils.utils.processCellForExport(params);
    },

    exportCSV() {
      const exportParams = {
        fileName: this.getFileName({
          serviceName: 'scenario-attributes',
          workpackageName: this.selectedWorkpackage.name,
          scenarioName: this.selectedScenario.name,
          fileNameDateFormat: this.getDateFormats.csvFileName,
        }),
        suppressQuotes: this.getCsvExport.suppressQuotes,
        columnSeparator: this.getCsvExport.columnSeparator,
        columnKeys: this.getAllVisibleDataColumns(),
        processHeaderCallback: this.mapHeaderNames, // Being called for each column
        processCellCallback: this.processCellCallback,
        skipColumnGroupHeaders: true,
      };

      this.gridApi.exportDataAsCsv(exportParams);
    },

    handleCheckboxChange(params) {
      const currentValue = get(params.data, params.colDef.field);
      params.setValue(!currentValue);
    },

    setupTracker() {
      const validationOptions = attributeEditorValidation.createProductValidationOptions({
        clientConfig: this.clientConfig,
        scenario: this.selectedScenario,
        workpackage: this.selectedWorkpackage,
        customAttributeIds: keys(this.currentCustomAttributes),
      });
      const validator = attributeEditorValidation.createProductValidator(validationOptions);
      tracker.init({
        data: cloneDeep(this.productsData),
        validation: {
          validateRow: validator.validateProduct,
          validateRows: validator.validateProducts,
        },
        // Defines functions like colName() { effectWhenModified }. What should happen when cell in col is altered.
        // i.e. parentProduct() { return assortment: false }
        reactions,
        api: this.gridApi,
      });
    },

    async discard() {
      this.isGridLoading = true;
      await this.resetAdditionalFields();
      this.resetChanges();
      // restore our previous saved state
      this.setProductData(cloneDeep(this.$options.productsData));
      this.refreshHeaders();
      this.setupTracker();

      if (!this.customHeaderFields.length) {
        // resize the columns if there are no custom attributes and refit them to the screen - avoids empty space in the grid
        this.gridApi.sizeColumnsToFit();
      }
      this.usedAttributeHeaders = [];
      this.onValidationFilterChange(null);

      this.isGridLoading = false;
    },

    async getScenarioProducts() {
      const scenarioProducts = await this.fetchScenarioProducts();

      // Create default eligibility data for each storeClass
      const defaultValues = this.storeClasses.reduce((defaultObj, sc) => {
        const storeClassId = sc._id;
        defaultObj[storeClassId] = {
          id: storeClassId,
          value: true,
        };

        return defaultObj;
      }, {});

      const valuesPerAttribute = {};

      const products = scenarioProducts.map(sp => {
        const obj = {};
        each(sp.customAttributes, attr => {
          // construct a lookup of custom attribute id to info about all values for it across products
          if (!valuesPerAttribute[attr.id]) {
            valuesPerAttribute[attr.id] = {
              forNewProducts: [],
              forExistingProducts: [],
            };
          }

          if (!sp._id || sp.isNewProduct) {
            // If product is new, push changed value to correct list
            valuesPerAttribute[attr.id].forNewProducts.push(attr.value);
          } else {
            valuesPerAttribute[attr.id].forExistingProducts.push(attr.value);
          }

          return set(obj, attr.id, attr.value);
        });

        // if there are no storeClasses defined, then ignore that field from the data
        if (this.storeClasses.length > 0) {
          this.setRestructuredEligibilityFromData(sp, obj, defaultValues);
        }

        each(keys(sp), key => {
          if (
            key === 'customAttributes' ||
            key === 'storeClassEligibility' ||
            key === 'restructuredEligibility'
          )
            return;
          set(obj, key, sp[key]);
        });
        return obj;
      });

      this.savedValuesPerCustomAttribute = mapValues(valuesPerAttribute, attrValues => {
        return {
          forNewProducts: uniq(attrValues.forNewProducts),
          forExistingProducts: uniq(attrValues.forExistingProducts),
        };
      });

      return products;
    },

    resetAttributesToClean() {
      const customAttributes = get(this.selectedScenario, 'customAttributes', []);
      const defaultCleanSetting = get(this.getClientConfig, 'features.cleanAttributesEnabled');

      // Set initial attributes to clean
      each(customAttributes, ca => {
        // Use default if clean flag has not been set
        const isClean = get(ca, 'clean', defaultCleanSetting);
        if (isClean) this.attributesToClean.add(ca.id);
      });
    },

    async setProductData(data) {
      this.productsData = data;

      // gridRowData exists only to be passed into the grid
      // This lets us modify productsData freely while tracker owns edits/validation state.
      // The better solution is to decouple lots of this from the grid itself, in AOV3-8550
      // That would let us have an abstract interface to get efficiently updated data.
      this.gridRowData = this.productsData.map(product => ({
        ...product,
      }));
    },

    async init() {
      // Init is also called after save, reset any associated data before this.
      this.resetChanges();
      // Only load furniture for standard workpackages
      if (!this.isTemplate) await this.fetchScenarioFurniture();
      else this.setScenarioFurniture({});

      // Re-fetch the scenario to pick up current custom attributes
      await this.loadScenario(this.selectedScenario._id);
      this.setSavedAttributes();
      this.setProductData(await this.getScenarioProducts());
      this.setupTracker();

      this.resetAttributesToClean();

      // backup data
      this.$options.productsData = cloneDeep(this.productsData);
      this.setKeyIdMap();

      // refresh headers (it is needed to properly apply dynamic store class eligibility headers and custom attributes)
      this.refreshHeaders();

      const catKeyCounts = tracker.getCounts('productCategoryKey');
      if (this.hasRestOfMarketEnabled && !isEmpty(catKeyCounts)) {
        await this.setCommonProductCategoryLevelKey(catKeyCounts);
      }

      // Add optional fields for CSV import
      this.updateFieldImportData(
        this.optionalFieldEnum.minimumFacings.field,
        this.showMinimumFacings
      );
      this.updateFieldImportData(
        this.optionalFieldEnum.minimumDistribution.field,
        this.showMinimumDistribution
      );

      // If its a fresh workpackage, also add modelled shrink rate as field to import
      if (this.isFreshWP) {
        this.updateFieldImportData('shrinkToSalesRatio', true);
      }

      // If dependent source is enabled, then add to import fields
      if (this.dependentProductsEnabled) {
        this.updateFieldImportData('dependentSource', true);
      }

      each(keys(this.selectedScenario.optionalAttributes.additionalFields), field => {
        this.updateFieldImportData(field, !!this.showAdditionalField(field));
      });

      // Automatically map pallet content if enabled
      if (this.hasPalletContentEnabled) this.optionalSchemaFields.add('palletContent');
      if (!this.transactionWeightDisabled)
        this.optionalSchemaFields.add('averageTransactionWeight');

      if (
        this.hasTransferPriceRefreshUpdatesPriceAndMarginEnabled &&
        !this.isFieldIgnoredForImport(fieldEnum.maintainOption)
      ) {
        this.optionalSchemaFields.add('maintainOption');
      }
      if (this.hasKPKUEnabled) {
        this.optionalSchemaFields.add('kpku');
      }
      if (this.productSeriesEnabled) {
        this.optionalSchemaFields.add('productSeries');
      }
      if (this.hasPeriodIDColumnEnabled) {
        this.optionalSchemaFields.add('periodID');
      }
    },

    async setCommonProductCategoryLevelKey(counts) {
      const [mostCommonKey] = maxBy(entries(counts), ([, count]) => count);
      const commonProductCategoryKey = Number(mostCommonKey);

      await this.fetchProductCategories({ categoryKeys: [commonProductCategoryKey] });
      const productCategoryLevelKey = get(
        this.getClientConfig,
        'assortmentGroups.productCategoryLevelKey'
      );
      this.commonProductCategoryLevelKey = get(
        this.productHierarchy,
        `${commonProductCategoryKey}.${productCategoryLevelKey}`,
        null
      );
    },

    cleanCustomAttributes() {
      const updatesByProductKey = {};

      this.gridApi.forEachNode(({ data }) => {
        this.attributesToClean.forEach(id => {
          const { productKey } = data;
          const currentValue = get(data, id);

          if (isNil(currentValue)) return;

          const formattedValue = this.hasCleanAttributesUpperCaseEnabled
            ? toUpper(currentValue)
            : toLower(currentValue);

          const sanitisedValue = formattedValue.trim();

          if (!updatesByProductKey[productKey]) {
            updatesByProductKey[productKey] = { productKey };
          }

          updatesByProductKey[productKey][id] = sanitisedValue;
        });
      });

      const updates = values(updatesByProductKey);

      if (updates.length) {
        this.updateRows(updates);
      }
    },

    cleanCustomAttributesInEditor() {
      this.cleanCustomAttributes();
    },

    splitAttributes(product) {
      // Get all attributes from the product
      const initialAttrs = pickBy(product, (v, k) => this.initialHeadersFields.includes(k));
      const customAttributes = pickBy(product, (v, k) => this.customHeaderFields.includes(k));
      const productWithoutCustomAttributes = pickBy(
        product,
        (v, k) =>
          !this.customHeaderFields.includes(k) && !this.storeClassEligibilityFields.includes(k)
      );

      const formattedInitialAttrs = {};
      Object.keys(initialAttrs).forEach(key => {
        formattedInitialAttrs[key] = initialAttrs[key] || null;
      });
      const formattedCustomAttributes = map(customAttributes, (value, id) => ({
        id,
        value: value || '',
      }));

      // Only set customAttributes if there are some
      if (size(formattedCustomAttributes)) {
        set(productWithoutCustomAttributes, 'customAttributes', formattedCustomAttributes);
      }

      // productWithoutCustomAttributes contains custom attributes now, if there are any,
      // would be good to rename this for readability
      return merge(
        omitBy(productWithoutCustomAttributes, (val, key) => {
          return key.startsWith('restructuredEligibility');
        }),
        formattedInitialAttrs
      );
    },

    splitStoreClassEligibility(product) {
      // helper function to split the {'restructuredEligibility.storeClassId.value': true/false}
      // to {storeClassEligibility: [id: a, value: true/false]}
      if (product.restructuredEligibility) {
        keys(product.restructuredEligibility).forEach(k => {
          // if store class eligibility is a string, set its value to an object key
          const flattenedValue = product[`restructuredEligibility.${k}.value`];
          if (!isUndefined(flattenedValue))
            product.restructuredEligibility[k].value = flattenedValue;
        });
      }
      // always make sure there is a temporary string property for restructured eligibility
      // so that it's possible to get unflattened eligibility
      keys(product.restructuredEligibility).forEach(k => {
        product[`restructuredEligibility.${k}.value`] = product.restructuredEligibility[k].value;
      });
      const storeClassEligibility = pickBy(product, (v, k) => {
        return this.storeClassEligibilityFields.includes(k);
      });
      const customAttributes = pickBy(product, (v, k) => this.customHeaderFields.includes(k));
      // need this for the mapValues to work
      const nestedEligibility = { p: storeClassEligibility };
      const formattedStoreClassEligibility = get(
        mapValues(nestedEligibility, eligibility => {
          const unflattenedEligibility = {};
          each(eligibility, (value, path) => set(unflattenedEligibility, path, value));
          return this.reStructureStoreClassEligibility(unflattenedEligibility);
        }),
        'p'
      );
      // Only set storeClass eligibility if there are some
      if (size(formattedStoreClassEligibility)) {
        set(product, 'storeClassEligibility', formattedStoreClassEligibility);
      }
      product = omitBy(product, (val, key) => {
        return key.startsWith('restructuredEligibility');
      });
      return omit(
        product,
        keys(storeClassEligibility).concat(keys(customAttributes), ['customAttributes'])
      );
    },

    /**
     * Prepares scenario updates to sync transfer price and product relationship data for scenario products
     * @param {Object} transferPriceRange - Deprecated: the transfer price range object.
     * Isn't actively used, since date range is effectively identified from periodIds.
     * @returns {Promise<void>}
     */
    async handleSyncProductData(transferPriceRange) {
      this.isSyncingProductData = true;
      // Remove last refreshed to avoid sending it to the server
      delete transferPriceRange.lastRefreshed;
      const maintainOptionByProductKey = mapValues(
        keyBy(
          map(this.productsData, p => {
            // get current state of rows in grid
            const row = this.gridApi.getRowNode(p.productKey);
            return {
              productKey: p.productKey,
              maintainOption: get(row.data, 'maintainOption', maintainOptions.doNothing),
            };
          }),
          'productKey'
        ),
        'maintainOption'
      );
      // Update the scenario
      const scenarioUpdates = {
        id: this.selectedScenario._id,
        transferPriceRange,
        maintainOptionByProductKey,
        periodIds: this.selectedWorkpackage.periodIds,
      };
      await this.syncProductDataAndRefresh(scenarioUpdates);
      this.isSyncingProductData = false;
    },

    bulkUpdateMaintain(newMaintainValue) {
      const newProductsWithUpdatedMaintainValue = map(this.productsData, product => {
        product.maintainOption = newMaintainValue;
        return product;
      });
      this.updateRows(newProductsWithUpdatedMaintainValue);
    },

    getChangedAttributeValues(alteredCustomAttributes) {
      const changedAttributeValues = {
        valuesAddedForNewProductsOnly: [],
        valuesDeletedFromNewProductsOnly: [],
        valuesAddedForProducts: [],
        valuesDeletedFromProducts: [],
        valuesChangedForProducts: alteredCustomAttributes,
      };

      each(alteredCustomAttributes, attrId => {
        const currentExisting = [];
        const currentNew = [];

        each(tracker.distinctValues(attrId), value => {
          const rows = tracker.getRowsWhere({ [attrId]: value });

          if (some(rows, row => row.isNewProduct)) {
            currentNew.push(value);
          }

          if (some(rows, row => !row.isNewProduct)) {
            currentExisting.push(value);
          }
        });

        const initialValues = get(this.savedValuesPerCustomAttribute, attrId, {});
        const initialExisting = initialValues.forExistingProducts || [];
        const initialNew = initialValues.forNewProducts || [];

        const valuesAddedForExistingProducts = difference(currentExisting, initialExisting);
        const valuesDeletedFromExistingProducts = difference(initialExisting, currentExisting);

        const valuesAddedForNewProducts = difference(currentNew, [
          ...initialExisting,
          ...initialNew,
          '', // don't count empty values as new
        ]);

        const valuesDeletedFromNewProducts = difference(initialNew, currentNew);

        const valuesAddedForNewProductsOnly = difference(
          valuesAddedForNewProducts,
          valuesAddedForExistingProducts
        );

        const valuesDeletedFromNewProductsOnly = difference(
          valuesDeletedFromNewProducts,
          valuesDeletedFromExistingProducts
        );

        if (valuesAddedForNewProductsOnly.length) {
          changedAttributeValues.valuesAddedForNewProductsOnly.push(attrId);
        }

        if (valuesDeletedFromNewProductsOnly.length) {
          changedAttributeValues.valuesDeletedFromNewProductsOnly.push(attrId);
        }

        if (valuesAddedForExistingProducts.length || valuesAddedForNewProductsOnly.length) {
          changedAttributeValues.valuesAddedForProducts.push(attrId);
        }

        if (valuesDeletedFromExistingProducts.length || valuesDeletedFromNewProductsOnly.length) {
          changedAttributeValues.valuesDeletedFromProducts.push(attrId);
        }
      });

      return changedAttributeValues;
    },

    formatProductsForSave() {
      // We get scenario products as a flat array of values, including custom attributes
      // Before saving, we re-group them under customAttributes
      // This does a single traversal and uses two picks to find the appropriate keys
      // Then we collect info about altered custom attributes to pass it to dependency tree

      const alteredCustomAttributes = [];
      const updatedProducts = pickBy(tracker.getChanges().edits, size);
      const productKeysMap = {};
      const products = map(updatedProducts, (productUpdate, productKey) => {
        const existingValues = this.keyIdMap[productKey]
          ? { _id: this.keyIdMap[productKey], productKey: Number(productKey) }
          : {};
        if (existingValues._id) productKeysMap[existingValues._id] = productKey;

        const attributesAfterSplit = this.splitAttributes(productUpdate);
        if (attributesAfterSplit.customAttributes) {
          alteredCustomAttributes.push(...attributesAfterSplit.customAttributes);
        }

        const updatedProduct = merge(
          {},
          attributesAfterSplit, // will return altered attributes as well
          this.splitStoreClassEligibility(productUpdate),
          existingValues
        );

        const copiedPriceMarginValues = this.handlePriceMarginValues(updatedProduct);

        return copiedPriceMarginValues;
      });

      // Get info about changed attribute values across products
      const changedAttributeValues = this.getChangedAttributeValues(
        uniq(map(alteredCustomAttributes, 'id'))
      );

      return { products, changedAttributeValues, productKeysMap };
    },

    async processCleanAttributesConfig(cleanOnSave) {
      const savedAttributes = get(this.selectedScenario, 'customAttributes', []);
      const defaultCleanSetting = get(this.getClientConfig, 'features.cleanAttributesEnabled');
      // Update 'clean' values for all saved custom attributes based on selections made in modal
      const customAttributes = map(savedAttributes, sa => {
        const isDeleted = includes(this.deletedAttributes, sa.id);
        // If the attribute has been deleted in the editor, save the original clean value
        // if it exists. Else, fallback to the config value
        return {
          ...sa,
          clean: !isDeleted
            ? this.attributesToClean.has(sa.id)
            : get(sa, 'clean', defaultCleanSetting),
        };
      });

      // Update the scenario
      const scenarioUpdates = {
        id: this.selectedScenario._id,
        customAttributes,
        cleanAttributesOnSave: cleanOnSave,
      };
      await this.updateScenario({ scenario: scenarioUpdates });

      // Re-fetch the scenario
      await this.loadScenario(this.selectedScenario._id);
      this.showSuccess(this.tkey('attributes.actions.saveSuccess'));
    },

    async saveChanges(commit = false) {
      const payloadData = await this.getFormattedDataForSaving();

      const deletedProductKeys = tracker.getChanges().removedRows;
      const rows = tracker.getRowsFromBaseline(deletedProductKeys);
      const deletedProducts = rows.map(row => pick(row, ['productKey', '_id']));

      try {
        this.isGridLoading = true;
        const result = await this.saveScenarioProducts({
          commit,
          ...payloadData,
          deletedCustomAttributes: tracker.getChanges().removedColumns,
          deletedProducts,
          // TODO: Check - do we need addedProducts?
          addedProducts: this.addedProducts,
          scenarioId: this.selectedScenario._id,
        });
        const { needsFeedback, output } = result.data;
        let outputRequiringFeedback = result.data.output;
        const everyWarningCanBeIgnored =
          needsFeedback && every(output, o => o.bypassUserConfirmation);
        if (needsFeedback) {
          // Remove the ones that are not meant to be visible
          outputRequiringFeedback = pickBy(result.data.output, o => !o.bypassUserConfirmation);
        }
        if (needsFeedback && !everyWarningCanBeIgnored) {
          this.dependencyTreeFeedback = outputRequiringFeedback;
          if (result.data.output.outdateOptimisedCanvases) {
            this.dependencyTreeWarningMessage = this.$t('dependencyTree.outdatedCanvasesWarning');
          }
          this.dependencyTreeModalOpen = true;
        } else if (needsFeedback && everyWarningCanBeIgnored) {
          // re-process user request but this time around, set commit on
          return this.saveChanges(true);
        } else {
          await this.processAfterSave(payloadData.addedCustomAttributes);
        }
      } catch (e) {
        console.error(e);
        this.showWarning();
      }
      this.isGridLoading = false;
    },

    async getFormattedDataForSaving() {
      // Check if custom attributes should be cleaned
      // Use the scenario cleanAttributesOnSave setting if it exists, else default to the config value
      const cleanOnSaveDefault = get(this.getClientConfig, 'features.cleanAttributesOnSaveEnabled');
      const cleanAttributesOnSave = get(
        this.selectedScenario,
        'cleanAttributesOnSave',
        cleanOnSaveDefault
      );
      if (cleanAttributesOnSave && this.attributesToClean.size) {
        this.cleanCustomAttributes();
      }

      const { products, changedAttributeValues, productKeysMap } = this.formatProductsForSave();
      // get altered custom attributes
      const addedCustomAttributes = this.formatNewAttributes();
      const addedStoreClassEligibility = this.formatNewStoreClassEligibility();

      return {
        addedCustomAttributes,
        addedStoreClassEligibility,
        products,
        changedAttributeValues,
        productKeysMap,
      };
    },

    async processAfterSave(addedCustomAttributes) {
      // No product should be left without storeClassEligibility defined
      this.productsWithoutEligibility = [];
      this.savedValuesPerCustomAttribute = {};

      this.setProductData(await this.getScenarioProducts());

      // we add new columns to selectedScenario and one scenario in vuex array
      const updatedScenario = await this.editCustomAttributes({
        scenario: this.selectedScenario,
        addedCustomAttributes,
        deletedCustomAttributes: this.deletedAttributes,
      });

      // update scenario
      const renamedCustomAttributes = isEmpty(this.updatedAttributes)
        ? {}
        : {
            customAttributes: updatedScenario.customAttributes.map(attribute => ({
              ...attribute,
              name: this.updatedAttributes[attribute.id]?.name || attribute.name,
            })),
          };
      const scenarioUpdates = {
        id: updatedScenario._id,
        ...this.currentScenarioStateDiff,
        ...renamedCustomAttributes,
      };

      await this.updateScenarioAndRefresh(scenarioUpdates);
      this.showSuccess(this.tkey('attributes.actions.saveSuccess'));
      await this.init();
    },

    async updateScenarioAndRefresh(scenarioUpdates) {
      // Update the scenario
      await this.updateScenario({ scenario: scenarioUpdates });
      // All attributes should have been saved so they are no longer new
      this.newAttributes = [];
      // Re-fetch the scenario
      await this.loadScenario(this.selectedScenario._id);

      // Set your custom attributes as they may have changed
      this.setSavedAttributes();
    },

    async syncProductDataAndRefresh(scenarioUpdates) {
      // Fetch transfer price data and product relationship data in parallel
      const [err, results] = await to(
        Promise.all([
          this.syncTransferPrice({ scenario: scenarioUpdates }),
          jobApi.runFunction('get-product-relationship', {
            snapshot_date: this.selectedWorkpackage.snapshotDate,
            period_ids: this.selectedWorkpackage.periodIds,
            products: this.productsData.map(p => ({
              productKey: p.productKey,
              productKeyDisplay: p.productKeyDisplay,
            })),
          }),
        ])
      );
      if (err) return;

      const [transferPrice, productRelationshipMap] = results;

      // Create relationship updates from productRelationshipMap
      const relationshipUpdates = Object.entries(productRelationshipMap).map(
        ([productKey, relationships]) => ({
          productKey: parseInt(productKey, 10),
          ...relationships, // This spreads parentProduct / palletContent / kpku / futureId based on enabled features
        })
      );

      // Combine transfer price updates with relationship updates
      // Create a map by productKey for easy merging
      const transferPriceUpdatesByKey = keyBy(transferPrice.productsUpdates, 'productKey');
      const relationshipUpdatesByKey = keyBy(relationshipUpdates, 'productKey');

      // Get all unique product keys from both sources
      const allProductKeys = uniq([
        ...Object.keys(transferPriceUpdatesByKey).map(Number),
        ...Object.keys(relationshipUpdatesByKey).map(Number),
      ]);

      // Merge updates for each product key
      const combinedUpdates = allProductKeys.map(productKey => {
        const transferPriceUpdate = transferPriceUpdatesByKey[productKey] || {};
        const relationshipUpdate = relationshipUpdatesByKey[productKey] || {};

        return {
          productKey,
          ...relationshipUpdate,
          ...transferPriceUpdate,
        };
      });

      this.updateRows(combinedUpdates);

      this.productsWithoutPurchasePriceInformation = transferPrice.productsWithoutPurchasePrice;
      this.currentScenarioStateDiff = { transferPriceRange: scenarioUpdates.transferPriceRange };
    },

    getAllVisibleDataColumns() {
      return this.gridApi
        .getColumns()
        .filter(
          c =>
            c.colId !== fieldEnum.deleteNewProduct &&
            !c.colDef.hide &&
            !ignoredFieldIdsForExport.has(c.colId)
        );
    },

    isFieldIgnoredForImport(fieldId) {
      return ignoredFieldIdsForImport.includes(fieldId);
    },

    formatNewAttributes() {
      // This method would ideally just return a set of attribute ids for both arrays
      // I've left it here to not have to change the server side but it could
      // be removed once scenario-products.updateAll is refactored.
      return this.newAttributes.map(attr => ({
        id: attr.colId,
        value: attr.colId,
        text: attr.headerName,
        clean: this.attributesToClean.has(attr.colId),
        source: attr.source,
        attributeKey: attr.attributeKey,
        attributeSource: attr.attributeSource,
      }));
    },

    reStructureStoreClassEligibility(product) {
      return keys(get(product, 'restructuredEligibility')).map(k => ({
        id: k,
        value: product.restructuredEligibility[k].value,
      }));
    },

    formatNewStoreClassEligibility() {
      // here we track the changes in case there are products without any eligibility set at all
      const productsWithoutEligibilitySet = new Set(this.productsWithoutEligibility.map(String));
      let updatesWithReStructuredEligibilityMap = {};
      const rowsByProductKey = keyBy(cloneDeep(tracker.getAllRows()), 'productKey');

      // proceed only if there are products without any eligibility set
      if (size(productsWithoutEligibilitySet)) {
        updatesWithReStructuredEligibilityMap = reduce(
          rowsByProductKey,
          (productsMapAcc, product, productKey) => {
            const productUpdatedOrMissingEligibilityData = productsWithoutEligibilitySet.has(
              String(productKey)
            );

            if (productUpdatedOrMissingEligibilityData) {
              productsMapAcc[product._id] = {
                _id: product._id,
                storeClassEligibility: this.reStructureStoreClassEligibility(product),
              };
            }
            return productsMapAcc;
          },
          {}
        );
      }
      return values(updatesWithReStructuredEligibilityMap);
    },

    syncValidationFilter() {
      const blockingErrorExists = tracker.gridHasBlockingValidations();
      const blockingStateChanged = blockingErrorExists !== this.blockingErrorExists;
      this.blockingErrorExists = blockingErrorExists;

      const criticalErrorIntroduced = blockingStateChanged && blockingErrorExists;

      if (criticalErrorIntroduced && !this.validationFilter) {
        // Immediately filter grid to critical issues
        this.validationFilter = severity.block;
        this.captureValidationFilterSnapshot(severity.block);
      }

      if (criticalErrorIntroduced) {
        this.showError(this.$t('validationErrors.criticalError'));
      }

      if (this.validationFilter) {
        this.gridApi?.onFilterChanged?.();
      }
    },

    captureValidationFilterSnapshot(filterMode) {
      this.validationFilterRowKeys = new Set(
        keys(tracker.state.rowIssueCounts).filter(rowKey =>
          tracker.rowHasFailedValidations(rowKey, filterMode)
        )
      );
    },

    doesExternalFilterPass(node) {
      const rowKey = String(node.data.productKey);
      const passesValidationFilter =
        !this.validationFilter || this.validationFilterRowKeys.has(rowKey);

      if (!passesValidationFilter) return false;

      const { productKeyDisplay, itemDescription } = node.data;
      const searchTerm = this.searchString;

      return productUtils.searchProductByKeyOrDescription(
        searchTerm,
        productKeyDisplay,
        itemDescription
      );
    },

    onValidationFilterChange(filterMode) {
      this.validationFilter = filterMode;
      if (filterMode) {
        this.captureValidationFilterSnapshot(filterMode);
      } else {
        this.validationFilterRowKeys = new Set();
      }
      this.gridApi?.onFilterChanged?.();
    },

    refreshHeaders() {
      // Async init can finish before onGridReady or after the grid was destroyed by a remount
      if (!this.gridApi) return;
      this.headers = this.setHeaders();
      this.saveColumnState();
    },

    onGridReady(params) {
      this.gridApi = params.api;

      this.headers = this.setHeaders();

      if (!this.customHeaderFields.length) {
        // resize the columns if there are no custom attributes and refit them to the screen - avoids empty space in the grid
        this.gridApi.sizeColumnsToFit();
      }

      if (this.validationFilter) this.gridApi.onFilterChanged();
    },

    isDateInvalid(params) {
      const dateValue = params.data[params.colDef.field];
      const dateFormat = this.getExcelDateFormat;
      return !this.isDateValid({ dateValue, dateFormat });
    },

    async createGroupedAttr(params) {
      const { attrName, grouping, sourceColumnId } = params;
      const addedKeys = await this.onCustomAttrAdd(attrName);

      const hashGrouping = {};
      grouping.forEach(group => {
        group.values.forEach(groupValue => {
          hashGrouping[groupValue] = group.name;
        });
      });
      const elId = addedKeys[0].id;
      this.gridApi.forEachNode(node => {
        node.data[elId] = hashGrouping[node.data[sourceColumnId]];
      });

      const productUpdates = this.productsData.map(product => {
        product[elId] = hashGrouping[product[sourceColumnId]];
        product = omit(product, ['transferredSalesFrom', 'transferredSalesTo']);
        return product;
      });
      this.updateRows(productUpdates);
    },

    clearFilters() {
      this.validationFilter = null;
      this.validationFilterRowKeys = new Set();
      this.searchString = '';
      this.gridApi.setFilterModel(null);
      this.gridApi.onFilterChanged();
    },

    showAdditionalField(field) {
      return (
        this.hasAdditionalFieldsEnabled &&
        get(this.selectedScenario, `optionalAttributes.additionalFields.${field}`)
      );
    },

    async resetAdditionalFields() {
      if (!this.hasAdditionalFieldsEnabled || !size(keys(this.backupAdditionalFields))) return;

      // rollback the optional attributes in case the user discard changes so we can hide the columns
      await this.setScenarioOptionalAttributes({
        useMinimumFacings: false,
        useMinimumDistribution: false,
        additionalFields: this.backupAdditionalFields,
      });

      each(keys(this.backupAdditionalFields), field => {
        // Since we have two types of Minimum Distribution this way we can use two differents columns in AgGrid
        const columnId =
          field === 'minimumDistribution' ? 'minimumDistributionAdditionalAttribute' : field;
        this.gridApi.setColumnsVisible([columnId], !!this.showAdditionalField(field));
      });
      this.backupAdditionalFields = {};
    },

    handlePriceMarginValues(product) {
      // We copy the values from theoretical fields to price and margin to keep it synced
      if (has(product, 'theoreticalPrice')) {
        product.price = product.theoreticalPrice;
      }

      if (has(product, 'theoreticalMargin')) {
        product.margin = product.theoreticalMargin;
      }

      // If we do not have theoretical fields we should not have price and margin since they are a copy
      if (!has(product, 'theoreticalPrice') && has(product, 'price')) {
        delete product.price;
      }

      if (!has(product, 'theoreticalMargin') && has(product, 'margin')) {
        delete product.margin;
      }
      return product;
    },

    buildInnerContext() {
      if (!this.selectedScenario) return {};

      const totalProducts = this.productsData?.length || 0;
      const newProducts = tracker.count({ isNewProduct: true });
      const errorProducts = tracker.countRowsWithFailedValidations(severity.error);
      const customAttributesArray = Object.values(this.currentCustomAttributes || {});
      const customAttrNames = customAttributesArray.map(a => a.name);

      const validationErrors = [];
      if (this.showProductsNotInTemplateWarning)
        validationErrors.push(`${this.totalProductsNotInTemplate} products not in template`);
      if (this.productsWithoutPurchasePriceInformation?.length > 0)
        validationErrors.push(
          `${this.productsWithoutPurchasePriceInformation.length} products missing purchase price information`
        );
      if (errorProducts > 0)
        validationErrors.push(`${errorProducts} products have validation errors`);

      // Derive visible filterable standard columns from the live grid so filter-column
      // has exact header names to work with. Tag numeric columns so the agent knows
      // which support comparison operators.
      const standardColumns = [];
      const numericStandardColumns = [];
      if (this.gridApi) {
        const columnStates = this.gridApi.getColumnState() || [];
        const hiddenColIds = new Set(columnStates.filter(cs => cs.hide).map(cs => cs.colId));
        const customAttrFieldIds = new Set(Object.keys(this.customAttributeColumnsByField || {}));

        (this.gridApi.getColumns() || []).forEach(col => {
          const colId = col.getColId();
          const colDef = col.getColDef();
          if (hiddenColIds.has(colId)) return;
          if (!colDef.headerName?.trim()) return; // skip icon/action columns
          if (customAttrFieldIds.has(colId)) return; // listed separately in customAttributes
          if (colDef.filter === false) return; // not filterable
          if (colId.startsWith('restructuredEligibility')) return; // store-class columns
          standardColumns.push(colDef.headerName);
          const isNumeric =
            colDef.filter === 'agNumberColumnFilter' || colDef.type === 'numericColumnNew';
          if (isNumeric) numericStandardColumns.push(colDef.headerName);
        });
      }

      // Derive active column filter summary and visible product count for the AI
      let activeColumnFilters = null;
      let filteredProductCount = null;
      if (this.gridApi) {
        const filterSummaries = [];
        const filterModel = this.filterModel || {};
        Object.entries(filterModel).forEach(([colId, filterDef]) => {
          const colDef = this.gridApi.getColumnDef(colId);
          const label = colDef?.headerName || colId;
          if (filterDef.filterType === 'set' && filterDef.values?.length) {
            filterSummaries.push(`${label} = ${filterDef.values.join(' or ')}`);
          } else if (filterDef.filterType === 'number') {
            filterSummaries.push(`${label} ${filterDef.type} ${filterDef.filter}`);
          } else if (filterDef.filterType === 'text' && filterDef.filter) {
            filterSummaries.push(`${label} contains "${filterDef.filter}"`);
          }
        });
        activeColumnFilters = filterSummaries.length ? filterSummaries.join(', ') : null;

        let count = 0;
        this.gridApi.forEachNodeAfterFilter(() => {
          count += 1;
        });
        filteredProductCount = count;
      }

      return {
        scenarioName: this.selectedScenario.name,
        scenarioProgress: this.formatScenarioProgress,
        totalProducts,
        newProducts,
        productsWithErrors: errorProducts,
        customAttributes: customAttrNames,
        standardColumns,
        numericStandardColumns,
        hasUnsavedChanges: this.hasDataChanges,
        canSave: this.hasDataChanges && !this.isEditingDisabled,
        isEditingDisabled: this.isEditingDisabled,
        currentSearchFilter: this.searchString || null,
        isShowingErrorsOnly: this.validationFilter === severity.error,
        activeColumnFilters,
        filteredProductCount,
        validationErrors,
        enabledFeatures: this.buildFeaturesList(),
      };
    },

    buildFeaturesList() {
      const features = [];
      const opts = this.selectedScenario?.optionalAttributes || {};
      if (opts.useMinimumFacings) features.push('minimumFacings');
      if (opts.useMinimumDistribution) features.push('minimumDistribution');
      if (this.hasTransferPriceEnabled) features.push('transferPricing');
      if (this.hasRestOfMarketEnabled) features.push('romProducts');
      if (this.templatesEnabled) features.push('productTemplates');
      return features;
    },
  },
};
</script>

<style lang="scss" scoped>
@import '@style/base/_variables.scss';

$height-of-row: 2.8rem;

.hidden-triangle {
  :deep(.error-triangle-container) {
    display: none;
  }
}

.assortment-table {
  // Added by the GH issue #2721 - to hide the sort order number during multi order
  // since currently we only have it when adding a new product and the header has no text
  // remove this if multi sort are being implemented to this ag-grid
  :deep(.ag-sort-order) {
    display: none !important;
  }

  // TODO AOV3-7012 - cleanup if not needed - already present in _tables.scss
  :deep(.error-triangle-container) {
    justify-content: flex-end;
    height: $height-of-row;
  }

  :deep(.ag-header-label-icon) {
    &.ag-sort-ascending-icon,
    &.ag-sort-descending-icon {
      margin-left: 0 !important;
    }

    &.ag-sort-order {
      display: none !important;
    }
  }

  .theme--light.v-data-table thead tr th {
    color: $assortment-table-header-colour;
    font-weight: normal;
  }

  thead {
    tr {
      height: 4.1rem;
    }
  }

  :deep(.mdi-checkbox-marked),
  :deep(.mdi-checkbox-blank-outline) {
    margin-top: 0.2rem;
  }

  .custom-header {
    border-top: 0.1rem solid $assortment-panel-border-divider-colour;
    z-index: 1;
  }

  td {
    font-size: 1.2rem !important;
    font-family: $assortment-regular-font;

    &:first-child {
      padding: 0 0.5rem !important;
    }

    &.fixed-column {
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    .v-input {
      margin-top: 0.2rem;
    }

    // Below cell-outer and inner containers are a workaround to hide scrollbars in the table cells
    // scrollbars on windows chrome affect cell height so need to be hidden

    .cell-outer-container {
      position: relative;
      overflow: hidden;
    }

    .cell-inner-container {
      overflow-x: scroll;
      overflow-y: hidden;

      &::-webkit-scrollbar {
        display: none;
      }
    }
  }

  .data-table-tab-container {
    .v-tabs-bar__content :first-child {
      margin-left: 0 !important;
    }

    .data-table-tab {
      padding: 0 1rem;
      justify-content: inherit;
      text-align: inherit;
      text-transform: none;
      min-width: 20rem;
      border: 0.1rem solid $assortment-panel-border-divider-colour;
      border-bottom: none;
      margin-left: 0.4rem;
      font-size: 1.2rem;
      font-weight: bold;
      color: $assortment-font-colour !important;
    }

    .data-table-tabs.theme--light.v-tabs > .v-tabs-bar {
      background-color: inherit !important;
    }

    .v-tabs-bar {
      height: 3rem;
    }

    .v-tab--selected {
      background-color: #ffffff;
    }

    .v-tab.v-tab {
      color: rgba(0, 0, 0, 0.54);
    }

    .v-tabs-slider {
      background-color: #ffffff;
    }
  }

  .pagination-container {
    padding: 1.5rem;

    .col {
      padding: 0;
    }
  }
}

#number-of-products {
  width: auto;
}

:deep(.error-cell) {
  background-color: $assortment-table-error-cell-background !important;

  &:hover {
    background-color: $assortment-table-error-cell-background !important;
  }
}

:deep(.diff-background) {
  background-color: $assortment-table-changed-cell-bg-colour;
}

:deep(.cell-error) {
  background-color: $rtls-red-30 !important;
}

:deep(.cell-warning) {
  background-color: $rtls-orange-20 !important;
}

:deep(.invalid-border) {
  border-bottom: 0.2rem solid $assortment-table-error-cell-border !important;
}

:deep(.ag-cell-not-inline-editing) {
  &.underlined-overridden {
    border-bottom: 0.2rem solid $assortment-primary-colour;
    border-left-width: 0.1rem !important;
    border-right-width: 0.1rem !important;
    font-style: italic;
    padding-left: 0.5rem !important;

    &.ag-cell-focus {
      padding-bottom: 0.1rem !important;
    }
  }
}

.disabled-cell {
  background-color: $assortment-draggable-disabled-color;
}

// Keep tooltip line breaks.
:deep(.ag-tooltip) {
  white-space: pre-line;
}

// fit page actions into ag-grid status panel to save vertical space
.page-actions {
  position: absolute;
  bottom: 3px;
  right: 0;
}

.buttons-header {
  height: 35px;
}

.info-message {
  :deep(.v-alert__prepend) {
    align-self: center;
  }
  :deep(.v-alert__close) {
    align-self: center;
    padding-bottom: 2px;
  }
}
</style>
